// ===============================================================
// 🎥 CallRoomPage.js — LIVORA FAST ROOM BOOTSTRAP VERSION
// - faster ZEGO bootstrap
// - supports zegoBootstrap from previous page
// - keeps current gift / chat / report logic
// - does not block room start on otherUser profile load
// ===============================================================

import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getFunctions, httpsCallable } from "firebase/functions";
import { auth, db } from "../firebase";
import {
  doc,
  getDoc,
  onSnapshot,
  collection,
  query,
  orderBy,
  limit,
} from "firebase/firestore";
import { ZegoExpressEngine } from "zego-express-engine-webrtc";
import SVGA from "svgaplayerweb";
import {
  PhoneOff,
  Mic,
  MicOff,
  Video,
  VideoOff,
  Flag,
  MessageCircle,
  RefreshCw,
  Gift,
} from "lucide-react";

import CallChatSheet from "./CallChatSheet";
import GiftStorePage from "../gifts/GiftStorePage";
import LiveGiftsOverlay from "../gifts/LiveGiftsOverlay";
import { GIFTS } from "../gifts/giftsCatalog";
import SensitiveContentGuard from "../components/privacy/SensitiveContentGuard";
import LivoraToast from "../components/common/LivoraToast";
import useLivoraToast from "../hooks/useLivoraToast";
import "./CallRoomPage.css";
import "./CallChatSheet.css";
import { callDebugLog } from "./callDebug";
import { getCallPlatformMode, logCallPlatformMode, logNativeCallStatusForLogging } from "./callPlatformMode";
import { logSnapCallFilterProbe } from "./snapCallFilterProbe";
import {
  invalidateSnapCameraKitInitProbe,
  runSnapCameraKitInitProbe,
} from "./snapCameraKitInitProbe";
import {
  invalidateSnapLensLoadProbe,
  runSnapLensLoadProbe,
} from "./snapLensLoadProbe";
import {
  cleanupSnapRealApplyProbe,
  invalidateSnapRealApplyProbe,
  runSnapRealApplyProbe,
} from "./snapRealApplyProbe";
import {
  cleanupZegoRawPumpProbe,
  invalidateZegoRawPumpProbe,
  runZegoRawPumpProbe,
} from "./zegoRawPumpProbe";
import {
  isActiveCallStatus,
  isExplicitCallExitSource,
  isOnCallRoomRoute,
  logBlockedCallExit,
  logBlockedCallNavigation,
  logCallNavigationGuardOk,
  logUnexpectedRouteLeave,
  shouldBlockCallRoomExit,
  shouldBlockCallRoomNavigation,
} from "./callNavigationGuard";
import {
  attachStreamToCallVideoElement,
  logStreamIdCheck,
  parseRemoteUserIdFromStreamId,
} from "./webZegoVideoAttach";
import {
  hasExitTriggered,
  isShutdownInProgress,
  isTerminalCallStatus,
  markCallPerf,
  registerShutdownHooks,
  requestEndCall,
  resetCallLifecycle,
  transitionCallLifecycle,
} from "./callLifecycleController";
import {
  ensureWebPlayAudioUnmuted,
  ensureWebPublishAudioUnmuted,
  logWebLocalMicTrack,
  logWebRemoteAudioAttached,
  logWebRemoteAudioTracks,
  stopWebRemoteAudioLevelMonitor,
  unlockWebCallAudio,
} from "./webCallAudio";
import {
  auditClearVideoElements,
  auditStopMediaStreamTracks,
  createShutdownId,
  scheduleWebPostCleanupChecks,
  shutdownAuditStep,
} from "./shutdownAudit";
import {
  beginWebCallStartup,
  cancelWebCallStartupIfBlocked,
  cancelWebCallStartupSession,
  cleanupWebCallMediaRegistry,
  clearWebCallMediaRegistry,
  getActiveWebCallSessionToken,
  getRegistryCleanupState,
  hasWebCallMediaRegistry,
  isWebCallStartupAllowed,
  markWebCallActive,
  markWebCallShutdownRequested,
  noteCallRoomPassiveUnmount,
  noteCallRoomRemount,
  tryClearPassiveTombstoneForActiveCall,
  registerWebCallMediaEngine,
  registerWebCallMediaElementsFromContainer,
  registerWebCallMediaLocalStream,
  registerWebCallMediaRemoteStream,
} from "./webCallMediaRegistry";
import {
  isWebCallHeartbeatRunning,
  sendWebMediaConnectedBilling,
  stopWebCallHeartbeat,
  syncWebCallHeartbeat,
} from "./webCallHeartbeat";

/**
 * Stable reason codes stored in Firestore (labels come from i18n) - must
 * exactly match the server allowlist in
 * functions/moderation/reportIdempotency.js's ALLOWED_REASONS. Reuses
 * existing translated keys where the meaning already matches closely
 * (harassment/scam/impersonation/other/sexual/minor_safety); "spam" has no
 * prior key anywhere in this codebase's locales, so it relies entirely on
 * its defaultValue until a translator adds one, exactly like every other
 * fallback-only string already used throughout this file.
 */
const REPORT_REASONS = [
  {
    code: "harassment",
    labelKey: "reports.reasons.harassment",
    defaultValue: "Harassment or threats",
  },
  {
    code: "nudity_sexual_misconduct",
    labelKey: "reports.reasons.sexual",
    defaultValue: "Nudity or sexual misconduct",
  },
  {
    code: "scam_fraud",
    labelKey: "reports.reasons.scam",
    defaultValue: "Scam or fraud",
  },
  {
    code: "spam",
    labelKey: "reports.reasons.spam",
    defaultValue: "Spam",
  },
  {
    code: "impersonation",
    labelKey: "reports.reasons.impersonation",
    defaultValue: "Impersonation",
  },
  {
    code: "underage_safety",
    labelKey: "reports.reasons.minor_safety",
    defaultValue: "Underage-safety concern",
  },
  {
    code: "other",
    labelKey: "reports.reasons.other",
    defaultValue: "Other",
  },
];

const MODERATION_DETAILS_MAX_LENGTH = 500;

/**
 * crypto.randomUUID() is available in every modern WebView/browser this app
 * targets; the timestamp+random fallback only guards a genuinely missing
 * API, not normal operation, and still satisfies the server's
 * isValidModerationActionId format (alphanumeric + hyphen, 8-128 chars).
 */
function generateModerationActionId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `modaction-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

const featuredGiftVideoItemCache = new Map();
const featuredGiftLoadingPromises = new Map();

function preloadFeaturedGiftSvga(assetUrl) {
  const url = String(assetUrl || "").trim();
  if (!url) return Promise.resolve(null);

  if (featuredGiftVideoItemCache.has(url)) {
    callDebugLog("[giftPreload] cache hit", { url });
    return Promise.resolve(featuredGiftVideoItemCache.get(url));
  }

  if (featuredGiftLoadingPromises.has(url)) {
    return featuredGiftLoadingPromises.get(url);
  }

  callDebugLog("[giftPreload] load start", { url });

  const promise = new Promise((resolve) => {
    try {
      const parser = new SVGA.Parser();
      parser.load(
        url,
        (videoItem) => {
          if (videoItem) {
            featuredGiftVideoItemCache.set(url, videoItem);
            callDebugLog("[giftPreload] load done", { url });
          } else {
            callDebugLog("[giftPreload] load fail", { url, reason: "empty videoItem" });
          }
          featuredGiftLoadingPromises.delete(url);
          resolve(videoItem || null);
        },
        () => {
          callDebugLog("[giftPreload] load fail", { url });
          featuredGiftLoadingPromises.delete(url);
          resolve(null);
        }
      );
    } catch (error) {
      callDebugLog("[giftPreload] load fail", { url, error });
      featuredGiftLoadingPromises.delete(url);
      resolve(null);
    }
  });

  featuredGiftLoadingPromises.set(url, promise);
  return promise;
}

const GIFT_WARM_PRELOAD_DELAY_MS = 1000;
const GIFT_WARM_PRELOAD_PRIORITY = { luxury: 0, big: 1, medium: 2, small: 3 };
const SECONDARY_UI_DELAY_MS = 1000;

function collectWarmPreloadSvgaUrls() {
  const seen = new Set();
  const items = [];

  for (const gift of GIFTS) {
    if (!gift?.enabled) continue;
    if (String(gift?.assetType || "").toLowerCase() !== "svga") continue;

    const assetUrl = String(gift?.assetUrl || "").trim();
    if (!assetUrl || seen.has(assetUrl)) continue;

    seen.add(assetUrl);
    const size = String(gift?.size || gift?.tab || "small").toLowerCase();
    items.push({ assetUrl, size });
  }

  items.sort(
    (a, b) =>
      (GIFT_WARM_PRELOAD_PRIORITY[a.size] ?? 9) -
      (GIFT_WARM_PRELOAD_PRIORITY[b.size] ?? 9)
  );

  return items.map((item) => item.assetUrl);
}

function FeaturedGiftStage({ gift }) {
  const { t } = useTranslation();
  const hostRef = useRef(null);
  const playerRef = useRef(null);
  const [svgaFailed, setSvgaFailed] = useState(false);

  useEffect(() => {
    if (!gift) return;

    let mounted = true;

    const cleanup = () => {
      try {
        if (playerRef.current) {
          try {
            playerRef.current.stopAnimation?.();
          } catch (_) {}
          try {
            playerRef.current.clear?.();
          } catch (_) {}
          try {
            playerRef.current.destroy?.();
          } catch (_) {}
        }
      } catch (e) {
        console.warn("FeaturedGiftStage cleanup warning:", e);
      }
      playerRef.current = null;
    };

    const assetType = String(gift?.assetType || "").toLowerCase();
    const assetUrl = String(gift?.assetUrl || "").trim();

    setSvgaFailed(false);

    if (assetType !== "svga" || !assetUrl || !hostRef.current) {
      return () => cleanup();
    }

    try {
      const player = new SVGA.Player(hostRef.current);
      playerRef.current = player;

      player.loops = 1;
      player.clearsAfterStop = true;
      player.fillMode = "Forward";

      const cachedVideoItem = featuredGiftVideoItemCache.get(assetUrl);

      if (cachedVideoItem) {
        callDebugLog("[giftPreload] cache hit", { url: assetUrl });
        try {
          if (!mounted) return () => cleanup();
          player.setVideoItem(cachedVideoItem);
          player.setContentMode("AspectFit");
          player.startAnimation();
        } catch (e) {
          console.error("FeaturedGiftStage startAnimation error:", e);
          if (mounted) setSvgaFailed(true);
          cleanup();
        }
      } else {
        preloadFeaturedGiftSvga(assetUrl)
          .then((videoItem) => {
            if (!mounted) return;
            if (!videoItem) {
              setSvgaFailed(true);
              cleanup();
              return;
            }

            player.setVideoItem(videoItem);
            player.setContentMode("AspectFit");
            player.startAnimation();
          })
          .catch((e) => {
            console.error("FeaturedGiftStage preload/start error:", e);
            if (mounted) setSvgaFailed(true);
            cleanup();
          });
      }
    } catch (e) {
      console.error("FeaturedGiftStage init error:", e);
      setSvgaFailed(true);
      cleanup();
    }

    return () => {
      mounted = false;
      cleanup();
    };
  }, [gift]);

  if (!gift) return null;

  const assetType = String(gift?.assetType || "").toLowerCase();
  const display = gift?.display || {};
  const isVideo = assetType === "video";
  const isImage = assetType === "image";
  const isSvga = assetType === "svga" && String(gift?.assetUrl || "").trim();

  if (isVideo && gift?.assetUrl) {
    return (
      <div
        className="callroom-featured-gift-video-stage"
        style={{
          position: "absolute",
          inset: 0,
          zIndex: display.zIndex || 999999,
          pointerEvents: "none",
        }}
      >
        <video
          src={gift.assetUrl}
          className={`callroom-featured-gift-video ${gift.animation || ""}`}
          autoPlay
          muted
          playsInline
          preload="auto"
          style={{
            position: "absolute",
            top: display.top || "0%",
            left: display.left || "0%",
            width: display.width || "100vw",
            height: display.height || "100vh",
            transform: `translate(${display.translateX || "0%"}, ${
              display.translateY || "0%"
            })`,
            objectFit: display.objectFit || "cover",
            pointerEvents: "none",
          }}
        />
      </div>
    );
  }

  const offsetX = gift?.offset
    ? gift?.isReceiver
      ? gift.offset.receiver
      : gift.offset.sender
    : -50;

  return (
    <div className="callroom-featured-gift-stage">
      <div className="callroom-featured-gift-card">
        <div
          className="callroom-featured-gift-media"
          style={{ transform: `translate(${offsetX}%, -50%)` }}
        >
          {isSvga && !svgaFailed ? (
            <div ref={hostRef} className="callroom-featured-gift-svga-host" />
          ) : isImage && gift?.assetUrl ? (
            <img
              src={gift.assetUrl}
              alt={gift.name || t("calls.gift", { defaultValue: "Gift" })}
              className="callroom-featured-gift-preview"
              draggable="false"
            />
          ) : gift?.previewUrl ? (
            <img
              src={gift.previewUrl}
              alt={gift.name || t("calls.gift", { defaultValue: "Gift" })}
              className="callroom-featured-gift-preview"
              draggable="false"
            />
          ) : (
            <div className="callroom-featured-gift-icon">{gift.icon || "💎"}</div>
          )}
        </div>

        <div className="callroom-featured-gift-name">{gift.name || t("calls.gift", { defaultValue: "Gift" })}</div>
        <div className="callroom-featured-gift-sender">
          {t("calls.gift_from", {
            sender: gift.sender || t("calls.unknown_user", { defaultValue: "User" }),
            defaultValue: "From {{sender}}",
          })}
        </div>
      </div>
    </div>
  );
}

const ZEGO_STREAM_AUDIO_BITRATE = 48;

function logCallPerf(label, t0, extra = {}) {
  callDebugLog(`[callPerf] ${label}`, {
    elapsed: Math.round(performance.now() - t0),
    ...extra,
  });
}

function logFirstPaintPerf(phase, roomEnterAt, extra = {}) {
  callDebugLog(`[callPerf] ${phase}`, {
    ms: Date.now() - roomEnterAt,
    ...extra,
  });
}

const ZEGO_CAMERA_AUDIO_CONFIG = {
  AEC: true,
  ANS: true,
  AGC: true,
};

function buildZegoStreamOptions(facingMode = "user", { simpleAudio = false } = {}) {
  const options = {
    camera: {
      video: {
        facingMode,
        quality: 2,
        frameRate: 24,
      },
      audio: simpleAudio ? true : ZEGO_CAMERA_AUDIO_CONFIG,
    },
    audioBitrate: ZEGO_STREAM_AUDIO_BITRATE,
  };
  return options;
}

function logAudioFixTrackState(role, label, tracks, callId = null) {
  const track = tracks[0] || null;
  callDebugLog(`[audioFix] ${label}`, {
    role,
    callId,
    audioTracksLength: tracks.length,
    enabled: track?.enabled,
    readyState: track?.readyState,
    muted: track?.muted,
  });
}

function getAudioTrackSnapshot(track, role, callId) {
  return {
    role,
    callId,
    enabled: track?.enabled,
    readyState: track?.readyState,
    muted: track?.muted,
  };
}

function attachLocalAudioTrackEventLogs(track, role, callId) {
  if (!track) return;

  const onMute = () => {
    callDebugLog(
      "[audioFix] local audio track mute event",
      getAudioTrackSnapshot(track, role, callId)
    );
  };
  const onUnmute = () => {
    callDebugLog(
      "[audioFix] local audio track unmute event",
      getAudioTrackSnapshot(track, role, callId)
    );
  };
  const onEnded = () => {
    callDebugLog(
      "[audioFix] local audio track ended event",
      getAudioTrackSnapshot(track, role, callId)
    );
  };

  if (typeof track.addEventListener === "function") {
    track.addEventListener("mute", onMute);
    track.addEventListener("unmute", onUnmute);
    track.addEventListener("ended", onEnded);
    return;
  }

  track.onmute = onMute;
  track.onunmute = onUnmute;
  track.onended = onEnded;
}

function logPublishFinalAudioState(localStream, role, callId) {
  const tracks = localStream?.getAudioTracks?.() || [];
  const track = tracks[0] || null;
  callDebugLog("[audioFix] publish final audio state", {
    role,
    callId,
    audioTracksLength: tracks.length,
    enabled: track?.enabled,
    readyState: track?.readyState,
    muted: track?.muted,
  });
}

async function waitForAudioTrackUnmuted(track, role, callId, maxWaitMs = 1000) {
  if (!track) return false;
  if (!track.muted) return true;

  callDebugLog(
    "[audioFix] male audio muted before publish, waiting",
    getAudioTrackSnapshot(track, role, callId)
  );

  return new Promise((resolve) => {
    const deadline = performance.now() + maxWaitMs;
    let settled = false;

    const finish = (unmuted) => {
      if (settled) return;
      settled = true;
      clearInterval(pollId);
      if (typeof track.removeEventListener === "function") {
        track.removeEventListener("unmute", onUnmuteOnce);
      }
      if (unmuted) {
        callDebugLog(
          "[audioFix] male audio unmuted before publish",
          getAudioTrackSnapshot(track, role, callId)
        );
      } else {
        console.warn(
          "[audioFix] male audio still muted after wait",
          getAudioTrackSnapshot(track, role, callId)
        );
      }
      resolve(unmuted);
    };

    const onUnmuteOnce = () => {
      if (!track.muted) finish(true);
    };

    if (typeof track.addEventListener === "function") {
      track.addEventListener("unmute", onUnmuteOnce);
    }

    const pollId = setInterval(() => {
      if (!track.muted) {
        finish(true);
        return;
      }
      if (performance.now() >= deadline) {
        finish(false);
      }
    }, 100);
  });
}

async function prepareMaleStreamForPublish(
  zg,
  localStream,
  hasAudio,
  micEnabled,
  role,
  callId,
  facingMode = "user"
) {
  let stream = localStream;
  let audio = hasAudio;
  let mic = micEnabled;

  if (role !== "male" || !audio) {
    logPublishFinalAudioState(stream, role, callId);
    return { localStream: stream, hasAudio: audio, micEnabled: mic };
  }

  let track = stream?.getAudioTracks?.()?.[0];
  if (!track) {
    logPublishFinalAudioState(stream, role, callId);
    return { localStream: stream, hasAudio: audio, micEnabled: mic };
  }

  if (track.muted) {
    const unmuted = await waitForAudioTrackUnmuted(track, role, callId, 1000);
    track = stream?.getAudioTracks?.()?.[0];

    if (!unmuted && track?.muted) {
      callDebugLog("[audioFix] male muted audio retry start", { role, callId });
      await destroyZegoLocalStream(zg, stream, {
        role,
        callId,
        phase: "male-muted-retry",
      });

      stream = await zg.createZegoStream(
        buildZegoStreamOptions(facingMode, { simpleAudio: true })
      );
      const validation = validateLocalStreamAudio(
        stream,
        role,
        "after male muted audio retry",
        callId
      );
      audio = validation.hasAudio;
      mic = validation.micEnabled;
      track = validation.tracks[0];

      callDebugLog("[audioFix] male muted audio retry result", {
        role,
        callId,
        audioTracksLength: validation.tracks.length,
        enabled: track?.enabled,
        readyState: track?.readyState,
        muted: track?.muted,
      });
    }
  }

  logPublishFinalAudioState(stream, role, callId);
  return { localStream: stream, hasAudio: audio, micEnabled: mic };
}

function validateLocalStreamAudio(localStream, role, label, callId = null) {
  const t0 = performance.now();
  logCallPerf("validateLocalStreamAudio start", t0, { role, callId, label });

  const tracks = localStream?.getAudioTracks?.() || [];
  logAudioFixTrackState(role, label, tracks, callId);

  const track = tracks[0];
  if (track && !track.enabled) {
    track.enabled = true;
    callDebugLog("[audioFix] re-enabled audio track", { role, label, callId });
  }

  if (track) {
    attachLocalAudioTrackEventLogs(track, role, callId);
  }

  const result = {
    tracks,
    hasAudio: tracks.length > 0,
    micEnabled: track ? track.enabled : false,
  };

  logCallPerf("validateLocalStreamAudio end", t0, {
    role,
    callId,
    label,
    hasAudio: result.hasAudio,
  });

  return result;
}

async function destroyZegoLocalStream(zg, localStream, meta = {}) {
  const t0 = performance.now();
  logCallPerf("destroyZegoLocalStream start", t0, meta);
  if (!localStream) {
    logCallPerf("destroyZegoLocalStream end", t0, { ...meta, skipped: true });
    return;
  }
  try {
    if (zg?.destroyStream) {
      zg.destroyStream(localStream);
      logCallPerf("destroyZegoLocalStream end", t0, { ...meta, via: "destroyStream" });
      return;
    }
  } catch (e) {
    console.warn("[audioFix] destroyStream warning:", e);
  }
  try {
    localStream.getTracks?.().forEach((track) => {
      try {
        track.stop();
      } catch (_) {}
    });
  } catch (_) {}
  logCallPerf("destroyZegoLocalStream end", t0, { ...meta, via: "stopTracks" });
}

function isMediaPermissionError(err) {
  if (!err) return false;
  if (err.isMediaPermission === true) return true;
  if (err.message === "GET_USER_MEDIA_FAILED") return true;
  const name = String(err.name || "");
  if (name === "NotAllowedError" || name === "PermissionDeniedError") {
    return true;
  }
  const code = String(err.code || "");
  const msg = String(err.message || "");
  return code.includes("1103064") || msg.includes("1103064");
}

async function createValidatedLocalStream(zg, role, facingMode = "user", callId = null) {
  const t0 = performance.now();
  logCallPerf("createValidatedLocalStream start", t0, { role, callId, facingMode });

  const optionsT0 = performance.now();
  const streamOptions = buildZegoStreamOptions(facingMode);
  logCallPerf("buildZegoStreamOptions done", optionsT0, { role, callId, facingMode });

  const streamT0 = performance.now();
  logCallPerf("createZegoStream start", streamT0, { role, callId });
  let localStream = await zg.createZegoStream(streamOptions);
  logCallPerf("createZegoStream end", streamT0, { role, callId });

  const validateT0 = performance.now();
  logCallPerf("audio validation start", validateT0, { role, callId });
  let validation = validateLocalStreamAudio(
    localStream,
    role,
    "after createZegoStream",
    callId
  );
  logCallPerf("audio validation end", validateT0, {
    role,
    callId,
    hasAudio: validation.hasAudio,
  });

  if (!validation.hasAudio && role === "male") {
    console.warn("[audioFix] male caller missing audio track, retrying", { role });
    const retryT0 = performance.now();
    logCallPerf("male retry start", retryT0, { role, callId });

    await destroyZegoLocalStream(zg, localStream, { role, callId, phase: "male-retry" });

    const delayT0 = performance.now();
    logCallPerf("male retry delay 300ms start", delayT0, { role, callId });
    await new Promise((resolve) => setTimeout(resolve, 300));
    logCallPerf("male retry delay 300ms end", delayT0, { role, callId });

    const retryStreamT0 = performance.now();
    logCallPerf("male retry createZegoStream start", retryStreamT0, { role, callId });
    localStream = await zg.createZegoStream(
      buildZegoStreamOptions(facingMode, { simpleAudio: true })
    );
    logCallPerf("male retry createZegoStream end", retryStreamT0, { role, callId });

    const retryValidateT0 = performance.now();
    logCallPerf("male retry audio validation start", retryValidateT0, { role, callId });
    validation = validateLocalStreamAudio(
      localStream,
      role,
      "after male retry createZegoStream",
      callId
    );
    logCallPerf("male retry audio validation end", retryValidateT0, {
      role,
      callId,
      hasAudio: validation.hasAudio,
    });
    logCallPerf("male retry end", retryT0, { role, callId, hasAudio: validation.hasAudio });
  }

  if (!validation.hasAudio) {
    console.error("[audioFix] no audio track after validation", { role });
  }

  logCallPerf("createValidatedLocalStream end", t0, {
    role,
    callId,
    hasAudio: validation.hasAudio,
  });

  return {
    localStream,
    hasAudio: validation.hasAudio,
    micEnabled: validation.micEnabled,
  };
}

function toSnapshotMillis(value) {
  if (!value) return 0;
  if (typeof value.toMillis === "function") return value.toMillis();
  return 0;
}

/** Fields that affect CallRoom UI / room lifecycle (excludes heartbeat churn). */
function getCallDocUiSignature(data) {
  if (!data) return "";

  return [
    String(data.status || ""),
    String(data.endedReason || ""),
    toSnapshotMillis(data.endedAt),
    String(data.maleUid || ""),
    String(data.femaleUid || ""),
    String(data.callType || ""),
    String(data.pricePerMinute ?? ""),
    toSnapshotMillis(data.startedAt),
  ].join("\u001f");
}

function mergeCallDocSnapshot(prev, callId, data) {
  const signature = getCallDocUiSignature(data);

  if (prev && prev.id === callId && prev.__uiSignature === signature) {
    return prev;
  }

  return {
    id: callId,
    ...(prev || {}),
    ...data,
    __uiSignature: signature,
  };
}

export default function CallRoomPage() {
  const { t } = useTranslation();
  const { toast, showToast } = useLivoraToast();
  const { callId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const returnTo = location?.state?.returnTo || "/home";
  const zegoBootstrap = location?.state?.zegoBootstrap || null;
  const zegoBootstrapRef = useRef(zegoBootstrap);
  zegoBootstrapRef.current = zegoBootstrap;

  const mountedRef = useRef(false);
  const webStartupInProgressRef = useRef(false);
  const startRoomEffectSessionRef = useRef(null);
  const meRef = useRef(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const [webStartGate, setWebStartGate] = useState(false);
  const [videoContainersReady, setVideoContainersReady] = useState(false);

  useEffect(() => {
    noteCallRoomRemount(callId);
    tryClearPassiveTombstoneForActiveCall(callId, callDataRef.current?.status || "ongoing");
  }, [callId]);

  useEffect(() => {
    console.log("[bootCheck] CallRoomPage mounted", { callId });
    logCallPlatformMode();
    void logNativeCallStatusForLogging();
  }, [callId]);

  const remoteVideoRef = useRef(null);
  const localVideoRef = useRef(null);

  const zegoRef = useRef(null);
  const localStreamRef = useRef(null);
  const localPreviewStreamRef = useRef(null);
  const localViewRef = useRef(null);
  const remoteViewRef = useRef(null);
  const remoteStreamRef = useRef(null);
  const publishedStreamIDRef = useRef(null);
  const playedStreamIDRef = useRef(null);

  const heartbeatRef = useRef(null); // legacy audit only; timer lives in webCallHeartbeat.js
  const callDocUnsubRef = useRef(null);
  const callDataRef = useRef(null);
  const timerRef = useRef(null);

  const exitTriggeredRef = useRef(false);
  const mediaCleanedRef = useRef(false);
  const mediaCleanupInProgressRef = useRef(false);
  const engineStartedRef = useRef(false);
  const exitCallRoomRef = useRef(null);
  const cleanupMediaRef = useRef(null);
  const webSessionTokenRef = useRef(null);
  const usingFrontCameraRef = useRef(true);

  const liveGiftTimersRef = useRef({});
  const featuredGiftTimerRef = useRef(null);
  const seenGiftEventIdsRef = useRef(new Set());
  const giftSoundRef = useRef(null);
  const giftWarmPreloadStartedRef = useRef(false);
  const callDocUiSignatureRef = useRef("");
  const secondaryUiTimerRef = useRef(null);
  const callSnapshotLogSigRef = useRef("");
  const hangupSyncStatusLogRef = useRef("");
  const secondaryUiScheduledRef = useRef(false);
  const firstPaintMarkersRef = useRef({
    localVideo: false,
    remoteVideo: false,
    roomReady: false,
  });

  const [callData, setCallData] = useState(null);
  const [role, setRole] = useState(null);
  const [me, setMe] = useState(null);
  const [otherUser, setOtherUser] = useState(null);

  useEffect(() => {
    callDataRef.current = callData;
  }, [callData]);

  const [callSeconds, setCallSeconds] = useState(0);
  const [loading, setLoading] = useState(true);
  const [roomReady, setRoomReady] = useState(false);
  const [remoteConnected, setRemoteConnected] = useState(false);
  const [callRoomStage, setCallRoomStage] = useState("loading");
  const [mediaPermissionFailed, setMediaPermissionFailed] = useState(false);

  useEffect(() => {
    setWebStartGate(false);
    setVideoContainersReady(false);
    engineStartedRef.current = false;
    webStartupInProgressRef.current = false;
    startRoomEffectSessionRef.current = null;
  }, [callId]);

  useEffect(() => {
    if (isActiveCallStatus(callData?.status) && me?.id && role) {
      setWebStartGate(true);
      tryClearPassiveTombstoneForActiveCall(callId, callData.status);
    }
  }, [callData?.status, me?.id, role, callId]);

  useEffect(() => {
    meRef.current = me;
  }, [me]);

  useLayoutEffect(() => {
    if (loading || !callData || !isActiveCallStatus(callData.status)) {
      setVideoContainersReady(false);
      return undefined;
    }
    const frame = requestAnimationFrame(() => {
      const ready = !!(remoteVideoRef.current && localVideoRef.current);
      if (ready) {
        setVideoContainersReady(true);
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [loading, callData, callId]);

  const roomEnterAtRef = useRef(0);
  const callPerfT0Ref = useRef(performance.now());

  const [micOn, setMicOn] = useState(true);
  const [cameraOn, setCameraOn] = useState(true);
  const [chatOpen, setChatOpen] = useState(false);
  const [giftStoreOpen, setGiftStoreOpen] = useState(false);
  const [secondaryUiReady, setSecondaryUiReady] = useState(false);
  const [localVideoAttached, setLocalVideoAttached] = useState(false);
  const [remoteVideoAttached, setRemoteVideoAttached] = useState(false);
  const [flippingCamera, setFlippingCamera] = useState(false);

  // Phase N8D: unified "Report and Block" moderation flow, reached only via
  // the options menu below - never a permanent dock/topbar button.
  const [optionsMenuOpen, setOptionsMenuOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [moderationStep, setModerationStep] = useState("reason"); // "reason" | "confirm"
  const [moderationReasonCode, setModerationReasonCode] = useState("");
  const [moderationDetails, setModerationDetails] = useState("");
  const [reportSending, setReportSending] = useState(false);

  const [isRemoteMain, setIsRemoteMain] = useState(true);
  const [isSwappingViews, setIsSwappingViews] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);
  const swapTimerRef = useRef(null);

  const [sendingGiftId, setSendingGiftId] = useState("");
  const [liveGifts, setLiveGifts] = useState([]);
  const [featuredGift, setFeaturedGift] = useState(null);

  const formatTime = (s) => {
    const m = Math.floor(Number(s || 0) / 60);
    const ss = Number(s || 0) % 60;
    return `${String(m).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
  };

  const getCallRoomStageLabel = useCallback(
    (stage) => {
      switch (stage) {
        case "token":
          return t("calls.stage_token", {
            defaultValue: "جاري تجهيز الاتصال...",
          });
        case "connecting":
          return t("calls.stage_connecting", {
            defaultValue: "جاري الاتصال بالغرفة...",
          });
        case "camera":
          return t("calls.stage_camera", {
            defaultValue: "جاري تشغيل الكاميرا...",
          });
        case "waiting_remote":
          return t("calls.waiting_remote_video", {
            defaultValue: "بانتظار ظهور فيديو الطرف الآخر...",
          });
        default:
          return t("calls.loading_room", {
            defaultValue: "جار فتح المكالمة...",
          });
      }
    },
    [t]
  );

  useEffect(() => {
    callPerfT0Ref.current = performance.now();
    roomEnterAtRef.current = Date.now();
    logFirstPaintPerf("CallRoom mount", roomEnterAtRef.current, {
      callId,
      hasBootstrap: !!zegoBootstrap?.token,
    });
  }, [callId, zegoBootstrap?.token]);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    const prevBodyTouchAction = body.style.touchAction;

    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    body.style.touchAction = "none";

    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
      body.style.touchAction = prevBodyTouchAction;
    };
  }, []);

  const openGiftStore = useCallback((e) => {
    e?.stopPropagation?.();
    if (!secondaryUiReady) return;
    setGiftStoreOpen(true);
  }, [secondaryUiReady]);
  const closeGiftStore = useCallback(() => setGiftStoreOpen(false), []);

  const scheduleSecondaryUi = useCallback((reason) => {
    if (secondaryUiScheduledRef.current) return;
    secondaryUiScheduledRef.current = true;

    logFirstPaintPerf("secondary UI scheduled", roomEnterAtRef.current, {
      callId,
      reason,
      delayMs: SECONDARY_UI_DELAY_MS,
    });

    if (secondaryUiTimerRef.current) {
      clearTimeout(secondaryUiTimerRef.current);
    }

    secondaryUiTimerRef.current = window.setTimeout(() => {
      setSecondaryUiReady(true);
      logFirstPaintPerf("secondary UI ready", roomEnterAtRef.current, {
        callId,
        reason,
      });
    }, SECONDARY_UI_DELAY_MS);
  }, [callId]);

  const navigateDuringWebStartup = useCallback(
    (to, options, reason, overrides = {}) => {
      const callStatus = callDataRef.current?.status || "";
      const blocked = shouldBlockCallRoomNavigation({
        callId,
        target: String(to),
        reason,
        callStatus,
        exitTriggered: exitTriggeredRef.current || hasExitTriggered(callId),
        webStartupInProgress: webStartupInProgressRef.current,
        hasWebStartupSession: !!getActiveWebCallSessionToken(callId),
        ...overrides,
      });
      if (blocked) {
        logBlockedCallNavigation({ callId, target: String(to), reason });
        return false;
      }
      navigate(to, options);
      return true;
    },
    [callId, navigate]
  );

  const guardedCallNavigate = navigateDuringWebStartup;

  const resetEngineLaunchRefs = useCallback((reason = "unknown") => {
    console.log(`[callExit] clearing engine launch refs callId=${callId} reason=${reason}`);
    engineStartedRef.current = false;
    setWebStartGate(false);
  }, [callId]);

  useEffect(() => {
    logCallNavigationGuardOk("no unexpected route leave during active call");
    const mountPath = `/call/${callId}`;
    return () => {
      const pathname = window.location?.pathname || "";
      const callStatus = callDataRef.current?.status || "";
      const realShutdown =
        exitTriggeredRef.current ||
        hasExitTriggered(callId) ||
        isShutdownInProgress(callId);
      const callStillLive =
        isActiveCallStatus(callStatus) ||
        webStartupInProgressRef.current ||
        !!getActiveWebCallSessionToken(callId);
      if (
        pathname !== mountPath &&
        callStillLive &&
        !realShutdown &&
        !isExplicitCallExitSource("component_unmount")
      ) {
        logUnexpectedRouteLeave({ callId, from: mountPath, to: pathname });
      }
    };
  }, [callId]);

  useEffect(() => {
    setLocalVideoAttached(false);
    setRemoteVideoAttached(false);
  }, [callId]);

  const toggleChatOpen = useCallback((e) => {
    e?.stopPropagation?.();
    if (!secondaryUiReady) return;
    setChatOpen((prev) => {
      const next = !prev;
      setControlsVisible(!next);
      return next;
    });
  }, [secondaryUiReady]);

  const handleStageBackgroundTap = useCallback(() => {
    if (chatOpen) {
      setChatOpen(false);
      setControlsVisible(true);
      return;
    }
    if (giftStoreOpen || reportOpen || optionsMenuOpen) return;
    setControlsVisible((prev) => !prev);
  }, [chatOpen, giftStoreOpen, reportOpen, optionsMenuOpen]);

  const playGiftSound = useCallback((gift) => {
    const soundUrl = String(gift?.soundUrl || "").trim();
    if (!soundUrl) return;

    try {
      if (giftSoundRef.current) {
        try {
          giftSoundRef.current.pause();
          giftSoundRef.current.currentTime = 0;
        } catch (_) {}
      }

      const audio = new Audio(soundUrl);
      audio.volume = 1;
      giftSoundRef.current = audio;

      audio.play().catch((err) => {
        console.warn("Gift sound play blocked:", err);
      });
    } catch (e) {
      console.warn("Gift sound init error:", e);
    }
  }, []);

  const pushSmallGiftToOverlay = useCallback(
    (gift) => {
      const safeId = String(gift?.id || "").trim();
      const safeName = String(gift?.name || t("calls.gift", { defaultValue: "Gift" })).trim();
      const safeIcon = gift?.icon || "🎁";
      const senderName = String(gift?.sender || t("calls.unknown_user", { defaultValue: "User" })).trim();
      const safeAssetType = String(gift?.assetType || "svga").trim();
      const safeAssetUrl = String(gift?.assetUrl || "").trim();
      const safePreviewUrl = String(gift?.previewUrl || "").trim();
      const safeSoundUrl = String(gift?.soundUrl || "").trim();

      if (!safeId) return;

      playGiftSound(gift);

      const existingTimers = liveGiftTimersRef.current[safeId];
      if (existingTimers?.leaveTimer) clearTimeout(existingTimers.leaveTimer);
      if (existingTimers?.removeTimer) clearTimeout(existingTimers.removeTimer);

      setLiveGifts((prev) => {
        const existing = prev.find((item) => item.id === safeId);

        if (existing) {
          return prev.map((item) =>
            item.id === safeId
              ? {
                  ...item,
                  assetType: safeAssetType,
                  assetUrl: safeAssetUrl,
                  previewUrl: safePreviewUrl,
                  soundUrl: safeSoundUrl,
                  count: Number(item.count || 1) + 1,
                  bump: true,
                  leaving: false,
                  exiting: false,
                }
              : item
          );
        }

        return [
          ...prev,
          {
            key: `gift_${safeId}_${Date.now()}`,
            id: safeId,
            name: safeName,
            icon: safeIcon,
            sender: senderName,
            assetType: safeAssetType,
            assetUrl: safeAssetUrl,
            previewUrl: safePreviewUrl,
            soundUrl: safeSoundUrl,
            count: 1,
            bump: true,
            leaving: false,
            exiting: false,
          },
        ];
      });

      setTimeout(() => {
        setLiveGifts((prev) =>
          prev.map((item) => (item.id === safeId ? { ...item, bump: false } : item))
        );
      }, 220);

      liveGiftTimersRef.current[safeId] = {
        leaveTimer: setTimeout(() => {
          setLiveGifts((prev) =>
            prev.map((item) =>
              item.id === safeId ? { ...item, leaving: true, exiting: true } : item
            )
          );
        }, 2200),
        removeTimer: setTimeout(() => {
          setLiveGifts((prev) => prev.filter((item) => item.id !== safeId));
          delete liveGiftTimersRef.current[safeId];
        }, 2550),
      };
    },
    [playGiftSound, t]
  );

  const pushLargeGiftToStage = useCallback(
    (gift) => {
      if (!gift) return;

      playGiftSound(gift);

      if (featuredGiftTimerRef.current) {
        clearTimeout(featuredGiftTimerRef.current);
        featuredGiftTimerRef.current = null;
      }

      const size = String(gift.size || "").toLowerCase();
      const duration =
        size === "luxury" ? 8000 : size === "large" ? 6000 : size === "big" ? 7000 : 5000;
      const receiverGiftDurationMultiplier = gift?.isReceiver ? 2.2 : 1;
      const finalDuration = Math.round(duration * receiverGiftDurationMultiplier);

      setFeaturedGift({
        id: String(gift.id || ""),
        name: String(gift.name || t("calls.gift", { defaultValue: "Gift" })),
        icon: gift.icon || "💎",
        sender: String(gift.sender || t("calls.unknown_user", { defaultValue: "User" })),
        assetType: gift.assetType || "svga",
        assetUrl: gift.assetUrl || "",
        previewUrl: gift.previewUrl || "",
        soundUrl: gift.soundUrl || "",
        isReceiver: !!gift.isReceiver,
        offset: gift.offset || null,
        display: gift.display || null,
        animation: gift.animation || "",
        startedAt: Date.now(),
      });

      featuredGiftTimerRef.current = setTimeout(() => {
        setFeaturedGift(null);
        featuredGiftTimerRef.current = null;
      }, finalDuration);
    },
    [playGiftSound, t]
  );

  const stopStreamTracks = (stream) => {
    if (!stream?.getTracks) return;
    stream.getTracks().forEach((track) => {
      try {
        track.stop();
      } catch (_) {}
    });
  };

  const clearVideoElementMedia = (containerRef) => {
    const container = containerRef?.current;
    if (!container) return;

    const videos =
      container.tagName === "VIDEO"
        ? [container]
        : Array.from(container.querySelectorAll("video"));

    videos.forEach((video) => {
      stopStreamTracks(video.srcObject);
      video.srcObject = null;
      try {
        video.pause();
      } catch (_) {}
    });
  };

  const cleanupMedia = useCallback(async ({
    endNative = false,
    reason = "unknown",
    shutdownId = null,
  } = {}) => {
    const shutdownOptions = {
      callStatus: callDataRef.current?.status || callData?.status || "",
      exitTriggered: exitTriggeredRef.current || hasExitTriggered(callId),
      shutdownInProgress: isShutdownInProgress(callId),
    };

    const shutdownMarked = markWebCallShutdownRequested(callId, reason, shutdownOptions);
    if (!shutdownMarked) {
      if (String(reason).includes("component_unmount") || reason === "startRoom_effect_cleanup") {
        console.log(`[shutdownAudit:web] component_unmount passive skip tombstone callId=${callId}`);
      }
      return;
    }

    if (mediaCleanedRef.current || mediaCleanupInProgressRef.current) {
      return;
    }

    const traceId = shutdownId || createShutdownId(callId, reason);

    const hasRegistryMedia = hasWebCallMediaRegistry(callId);

    mediaCleanupInProgressRef.current = true;
    shutdownAuditStep(traceId, "cleanupMedia_start", true, { endNative, reason });
    callDebugLog("[cleanupFix] cleanup start", { endNative, reason });

    try {
      invalidateSnapCameraKitInitProbe(reason || "cleanup_media");
      invalidateSnapLensLoadProbe(reason || "cleanup_media");
      invalidateSnapRealApplyProbe(reason || "cleanup_media");
      void cleanupSnapRealApplyProbe(reason || "cleanup_media");
      invalidateZegoRawPumpProbe(reason || "cleanup_media");
      void cleanupZegoRawPumpProbe(reason || "cleanup_media");
      stopWebCallHeartbeat(callId, reason || "cleanup_media");
      heartbeatRef.current = null;
      shutdownAuditStep(traceId, "heartbeat_interval_cleared", true);

      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
        shutdownAuditStep(traceId, "call_timer_interval_cleared", true);
      } else {
        shutdownAuditStep(traceId, "call_timer_interval_cleared", true, { already: true });
      }

      if (remoteViewRef.current?.stop) {
        try {
          remoteViewRef.current.stop();
          shutdownAuditStep(traceId, "remote_view_stop", true);
        } catch (e) {
          shutdownAuditStep(traceId, "remote_view_stop", false, { error: e?.message || e });
        }
      }
      remoteViewRef.current = null;

      if (localViewRef.current?.stop) {
        try {
          localViewRef.current.stop();
          shutdownAuditStep(traceId, "local_view_stop", true);
        } catch (e) {
          shutdownAuditStep(traceId, "local_view_stop", false, { error: e?.message || e });
        }
      }
      localViewRef.current = null;

      shutdownAuditStep(traceId, "zego_web_cleanup_start", true);
      callDebugLog("[cleanupFix] zego cleanup start");

      await cleanupWebCallMediaRegistry(callId, traceId, reason, shutdownOptions);

      if (zegoRef.current && playedStreamIDRef.current) {
        try {
          stopWebRemoteAudioLevelMonitor(playedStreamIDRef.current);
          zegoRef.current.stopPlayingStream(playedStreamIDRef.current);
          shutdownAuditStep(traceId, "ref_fallback_stopPlayingStream", true, {
            streamID: playedStreamIDRef.current,
          });
        } catch (e) {
          shutdownAuditStep(traceId, "ref_fallback_stopPlayingStream", false, {
            error: e?.message || e,
          });
        }
      }

      if (zegoRef.current && publishedStreamIDRef.current) {
        try {
          zegoRef.current.stopPublishingStream(publishedStreamIDRef.current);
          shutdownAuditStep(traceId, "ref_fallback_stopPublishingStream", true, {
            streamID: publishedStreamIDRef.current,
          });
        } catch (e) {
          shutdownAuditStep(traceId, "ref_fallback_stopPublishingStream", false, {
            error: e?.message || e,
          });
        }
      }

      playedStreamIDRef.current = null;

      auditStopMediaStreamTracks(remoteStreamRef.current, traceId, "remote");
      remoteStreamRef.current = null;
      auditStopMediaStreamTracks(localStreamRef.current, traceId, "local");
      localStreamRef.current = null;
      localPreviewStreamRef.current = null;

      auditClearVideoElements(localVideoRef, traceId, "local");
      auditClearVideoElements(remoteVideoRef, traceId, "remote");

      if (zegoRef.current && callId) {
        try {
          await zegoRef.current.logoutRoom(callId);
          shutdownAuditStep(traceId, "ref_fallback_leaveRoom", true, { callId });
        } catch (e) {
          shutdownAuditStep(traceId, "ref_fallback_leaveRoom", false, { error: e?.message || e });
        }
      }

      if (zegoRef.current?.destroyEngine) {
        try {
          zegoRef.current.destroyEngine();
          shutdownAuditStep(traceId, "ref_fallback_destroyEngine", true);
        } catch (e) {
          shutdownAuditStep(traceId, "ref_fallback_destroyEngine", false, { error: e?.message || e });
        }
      }

      zegoRef.current = null;
      publishedStreamIDRef.current = null;
      clearWebCallMediaRegistry(callId);

      if (giftSoundRef.current) {
        try {
          giftSoundRef.current.pause();
          giftSoundRef.current.currentTime = 0;
        } catch (_) {}
        giftSoundRef.current = null;
      }
    } catch (e) {
      shutdownAuditStep(traceId, "cleanupMedia_error", false, { error: e?.message || e });
      console.warn("cleanupMedia warning:", e);
    } finally {
      mediaCleanupInProgressRef.current = false;
      mediaCleanedRef.current = true;
      engineStartedRef.current = false;
      setRoomReady(false);
      setRemoteConnected(false);
      setLocalVideoAttached(false);
      setRemoteVideoAttached(false);
      shutdownAuditStep(traceId, "cleanupMedia_done", true);
      callDebugLog("[cleanupFix] cleanup done");

      scheduleWebPostCleanupChecks(
        traceId,
        () => {
          const registryState = getRegistryCleanupState(callId);
          return {
            ...registryState,
            zegoEngineNull: registryState.zegoEngineNull && !zegoRef.current,
            heartbeatCleared: !isWebCallHeartbeatRunning(callId),
            timerCleared: !timerRef.current,
            engineStarted: !!engineStartedRef.current,
            mediaCleaned: !!mediaCleanedRef.current,
          };
        },
        callId
      );
    }
  }, [callId]);

  cleanupMediaRef.current = cleanupMedia;

  useEffect(() => {
    registerShutdownHooks({
      stopWebZego: async (targetCallId, source, shutdownId) => {
        if (targetCallId !== callId) return;
        if (mediaCleanedRef.current || mediaCleanupInProgressRef.current) {
          shutdownAuditStep(shutdownId, "stop_web_zego_skipped_already_clean", true, {
            callId: targetCallId,
          });
          return;
        }
        await cleanupMedia({
          endNative: false,
          reason: `shutdown:${source}`,
          shutdownId,
        });
      },
      unsubscribeCallDoc: async (targetCallId, shutdownId) => {
        if (targetCallId !== callId) return;
        if (callDocUnsubRef.current) {
          try {
            callDocUnsubRef.current();
            callDocUnsubRef.current = null;
            shutdownAuditStep(shutdownId, "call_onSnapshot_unsubscribed", true, {
              callId: targetCallId,
            });
          } catch (e) {
            shutdownAuditStep(shutdownId, "call_onSnapshot_unsubscribed", false, {
              error: e?.message || e,
            });
          }
        }
      },
      navigateAway: (targetCallId, source, shutdownId) => {
        if (targetCallId && targetCallId !== callId) return;
        const allowed = guardedCallNavigate(returnTo, { replace: true }, `shutdown:${source}`, {
          userInitiated: hasExitTriggered(callId),
        });
        if (allowed) {
          callDebugLog("[callEnd] navigated after end", {
            callId: targetCallId,
            source: "shutdown",
            shutdownId,
          });
        }
      },
      shouldNavigateAway: (targetCallId, source) => {
        if (targetCallId && targetCallId !== callId) return true;
        if (isExplicitCallExitSource(`shutdown:${source}`) || isExplicitCallExitSource(source)) {
          return true;
        }
        const callStatus = callDataRef.current?.status || "";
        if (
          shouldBlockCallRoomNavigation({
            callId,
            target: returnTo,
            reason: `shutdown:${source}`,
            callStatus,
            exitTriggered: exitTriggeredRef.current || hasExitTriggered(callId),
            webStartupInProgress: webStartupInProgressRef.current,
            hasWebStartupSession: !!getActiveWebCallSessionToken(callId),
          })
        ) {
          logBlockedCallNavigation({
            callId,
            target: returnTo,
            reason: `shutdown:${source}`,
          });
          return false;
        }
        return true;
      },
      getWebCleanupState: () => {
        const registryState = getRegistryCleanupState(callId);
        return {
          ...registryState,
          zegoEngineNull: registryState.zegoEngineNull && !zegoRef.current,
          heartbeatCleared: !isWebCallHeartbeatRunning(callId),
          timerCleared: !timerRef.current,
          engineStarted: !!engineStartedRef.current,
          mediaCleaned: !!mediaCleanedRef.current,
        };
      },
    });
    return () => registerShutdownHooks({});
  }, [callId, cleanupMedia, guardedCallNavigate, returnTo]);

  const exitCallRoom = useCallback(
    ({
      source = "unknown",
      invokeEndCall = false,
      endCallReason = null,
      endedReason = null,
      endNativeInCleanup = false,
      terminalData = null,
    } = {}) => {
      const observedStatus = String(terminalData?.status || "");
      const terminalObserved = isTerminalCallStatus(terminalData);
      const stillOnCallRoute = isOnCallRoomRoute(callId);

      if (exitTriggeredRef.current || hasExitTriggered(callId)) {
        if (!terminalObserved || !stillOnCallRoute || isShutdownInProgress(callId)) {
          callDebugLog("[callEnd] exit skipped duplicate", { callId, source });
          return;
        }
        console.log("[hangupSync] terminal exit retry while still on call route", {
          callId,
          source,
        });
      }

      if (terminalData) {
        callDataRef.current = {
          ...(callDataRef.current || {}),
          id: callId,
          ...terminalData,
        };
      }

      const callStatus = observedStatus || callDataRef.current?.status || "";
      if (
        shouldBlockCallRoomExit({
          source,
          callStatus,
          observedStatus,
          webStartupInProgress: webStartupInProgressRef.current,
          hasWebStartupSession: !!getActiveWebCallSessionToken(callId),
          invokeEndCall: !!invokeEndCall,
        })
      ) {
        logBlockedCallExit({ callId, source });
        return;
      }

      exitTriggeredRef.current = true;
      const shutdownId = createShutdownId(callId, source);
      shutdownAuditStep(shutdownId, invokeEndCall ? "user_clicked_hangup" : "firestore_terminal_observed", true, {
        endedReason: endedReason || null,
      });

      if (terminalObserved && !invokeEndCall) {
        console.log(`[hangupSync] remote hangup received, closing local call callId=${callId}`);
      }

      callDebugLog(
        "[callEnd]",
        invokeEndCall ? "local hangup requested" : "remote/status ended observed",
        { callId, source, endedReason: endedReason || null }
      );

      stopWebCallHeartbeat(callId, source || "exit_call_room");
      heartbeatRef.current = null;

      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

      resetEngineLaunchRefs(source || "exit_call_room");

      void requestEndCall({
        callId,
        source,
        userInitiated: !!invokeEndCall,
        navigateAfter: !shouldBlockCallRoomExit({
          source,
          callStatus,
          observedStatus,
          webStartupInProgress: webStartupInProgressRef.current,
          hasWebStartupSession: !!getActiveWebCallSessionToken(callId),
          invokeEndCall: !!invokeEndCall,
        }),
        endCallReason: endCallReason || (invokeEndCall ? "hangup" : null),
        invokeEndCall,
        failureMustEnd: false,
        shutdownId,
      });
    },
    [callId, resetEngineLaunchRefs]
  );

  exitCallRoomRef.current = exitCallRoom;

  const requestHangup = useCallback(
    (source, endCallReason = "hangup") => {
      if (exitTriggeredRef.current || hasExitTriggered(callId)) {
        return;
      }

      console.log(
        `[hangupSync] user clicked hangup callId=${callId} uid=${auth.currentUser?.uid || ""} platform=web source=${source}`
      );

      exitTriggeredRef.current = true;
      const shutdownId = createShutdownId(callId, source || "web_hangup");
      shutdownAuditStep(shutdownId, "user_clicked_hangup", true, { source });

      stopWebCallHeartbeat(callId, source || "web_hangup");
      heartbeatRef.current = null;
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      resetEngineLaunchRefs(source || "web_hangup");

      void requestEndCall({
        callId,
        source: source || "web_hangup",
        userInitiated: true,
        navigateAfter: true,
        endCallReason,
        invokeEndCall: true,
        shutdownId,
      });
    },
    [callId, resetEngineLaunchRefs]
  );

  useEffect(() => {
    exitTriggeredRef.current = false;
    mediaCleanedRef.current = false;
    mediaCleanupInProgressRef.current = false;
    callDocUiSignatureRef.current = "";
    secondaryUiScheduledRef.current = false;
    resetCallLifecycle(callId, {
      role,
      platformMode: "web",
    });
    markCallPerf(callId, "callroom_mount", { role });
    callSnapshotLogSigRef.current = "";
    hangupSyncStatusLogRef.current = "";
    firstPaintMarkersRef.current = {
      localVideo: false,
      remoteVideo: false,
      roomReady: false,
    };
    console.log("[callRoomUI] V2.3 active");
    setSecondaryUiReady(false);
    setChatOpen(false);
    setControlsVisible(true);
    if (secondaryUiTimerRef.current) {
      clearTimeout(secondaryUiTimerRef.current);
      secondaryUiTimerRef.current = null;
    }
    roomEnterAtRef.current = Date.now();
    callDebugLog("[callDebug] entering CallRoomPage", { callId });
    callDebugLog("[callDebug] currentUser uid", auth.currentUser?.uid);
    callDebugLog("[callDebug] callId", callId);
  }, [callId]);

  useEffect(() => {
    return () => {
      if (secondaryUiTimerRef.current) {
        clearTimeout(secondaryUiTimerRef.current);
        secondaryUiTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!callData) return;
    callDebugLog("[callDebug] callData status", callData.status);
    callDebugLog("[callDebug] callerId", callData.maleUid);
    callDebugLog("[callDebug] receiverId", callData.femaleUid);
    callDebugLog("[callDebug] role", role);
  }, [callData, role]);

  const toggleMic = useCallback((e) => {
    e?.stopPropagation?.();
    const stream = localStreamRef.current;
    if (!stream?.getAudioTracks) {
      console.error("[audioFix] toggle mic failed no audio track", {
        reason: "no getAudioTracks",
      });
      setMicOn(false);
      return;
    }
    const track = stream.getAudioTracks()[0];
    if (!track) {
      console.error("[audioFix] toggle mic failed no audio track");
      setMicOn(false);
      return;
    }

    const next = !track.enabled;
    track.enabled = next;
    setMicOn(next);
  }, []);

  const toggleCamera = useCallback((e) => {
    e?.stopPropagation?.();
    const stream = localStreamRef.current;
    if (!stream?.getVideoTracks) return;
    const track = stream.getVideoTracks()[0];
    if (!track) return;

    const next = !track.enabled;
    track.enabled = next;

    try {
      if (zegoRef.current?.enableCamera) {
        zegoRef.current.enableCamera(next);
      }
    } catch (err) {
      console.warn("enableCamera warning:", err);
    }

    setCameraOn(next);
  }, []);

  const flipCamera = useCallback(
    async (e) => {
      e?.stopPropagation?.();
      if (!zegoRef.current || !publishedStreamIDRef.current || flippingCamera) return;

      try {
        setFlippingCamera(true);

        const nextFront = !usingFrontCameraRef.current;
        usingFrontCameraRef.current = nextFront;

        try {
          if (localViewRef.current?.stop) localViewRef.current.stop();
        } catch (_) {}

        try {
          if (zegoRef.current && publishedStreamIDRef.current) {
            zegoRef.current.stopPublishingStream(publishedStreamIDRef.current);
          }
        } catch (err) {
          console.warn("stopPublishingStream before flip warning:", err);
        }

        await destroyZegoLocalStream(zegoRef.current, localStreamRef.current, {
          role,
          callId,
          phase: "flipCamera",
        });
        localStreamRef.current = null;
        localPreviewStreamRef.current = null;

        let { localStream, hasAudio, micEnabled } = await createValidatedLocalStream(
          zegoRef.current,
          role,
          nextFront ? "user" : "environment",
          callId
        );

        ({ localStream, hasAudio, micEnabled } = await prepareMaleStreamForPublish(
          zegoRef.current,
          localStream,
          hasAudio,
          micEnabled,
          role,
          callId,
          nextFront ? "user" : "environment"
        ));

        localStreamRef.current = localStream;
        registerWebCallMediaLocalStream(
          callId,
          localStream,
          publishedStreamIDRef.current,
          webSessionTokenRef.current
        );
        setMicOn(hasAudio ? micEnabled : false);

        const newView = zegoRef.current.createLocalStreamView(localStream);
        localViewRef.current = newView;
        const flipAttachResult = await attachStreamToCallVideoElement({
          container: localVideoRef.current,
          stream: localStream,
          streamID: publishedStreamIDRef.current || "local",
          scope: "local",
          mirror: nextFront,
        });
        localPreviewStreamRef.current = flipAttachResult.mediaStream || null;
        if (flipAttachResult.ok) {
          setLocalVideoAttached(true);
        }
        registerWebCallMediaElementsFromContainer(
          callId,
          publishedStreamIDRef.current,
          localVideoRef.current,
          "local",
          webSessionTokenRef.current
        );

        if (hasAudio) {
          callDebugLog("[audioFix] publish with audio track", {
            role,
            source: "flipCamera",
          });
        } else {
          console.error("[audioFix] flipCamera publish without audio track", {
            role,
          });
        }
        zegoRef.current.startPublishingStream(publishedStreamIDRef.current, localStream);
      } catch (err) {
        console.warn("flipCamera error:", err);
      } finally {
        setFlippingCamera(false);
      }
    },
    [flippingCamera, role, callId]
  );

  useEffect(() => {
    return () => {
      if (swapTimerRef.current) {
        clearTimeout(swapTimerRef.current);
        swapTimerRef.current = null;
      }
    };
  }, []);

  const handlePipSwap = useCallback(
    (e) => {
      e?.stopPropagation?.();
      if (isSwappingViews) return;

      setIsSwappingViews(true);
      setIsRemoteMain((prev) => {
        const next = !prev;
        console.log(`[callRoomUI] PiP swap isRemoteMain=${next}`);
        return next;
      });

      if (swapTimerRef.current) {
        clearTimeout(swapTimerRef.current);
      }
      swapTimerRef.current = window.setTimeout(() => {
        setIsSwappingViews(false);
        swapTimerRef.current = null;
      }, 250);
    },
    [isSwappingViews]
  );

  /**
   * Phase N8D1: one stable moderationActionId per call-room mount, generated
   * lazily on first use and reused for every attempt (including any retry)
   * of the moderation action in this call - never regenerated per attempt.
   * A fresh id is only ever needed for a genuinely new call (new callId),
   * which happens naturally on remount since this ref is recreated then.
   */
  const moderationActionIdRef = useRef(null);
  function getOrCreateModerationActionId() {
    if (!moderationActionIdRef.current) {
      moderationActionIdRef.current = generateModerationActionId();
    }
    return moderationActionIdRef.current;
  }

  /**
   * Phase N8D1: the unified "Report and Block" action, reachable only after
   * an explicit reason selection AND an explicit final confirmation (never
   * a one-tap action). Report and block are now a single atomic
   * server-side action (reportAndBlockUser) - this component never calls
   * reportUser or blockUser separately for this in-call flow. The call
   * always ends exactly once, through the EXISTING guarded requestHangup
   * (never a bespoke cleanupMedia call of this handler's own), regardless
   * of whether the combined server action succeeded.
   */
  const submitModerationAction = useCallback(async () => {
    if (!moderationReasonCode || !otherUser?.id || !callId || reportSending) {
      return;
    }
    if (
      exitTriggeredRef.current ||
      hasExitTriggered(callId) ||
      isShutdownInProgress(callId)
    ) {
      // The call is already ending/ended through some other path (e.g. a
      // remote hangup arrived while this sheet was open) - never race a
      // second termination attempt against it.
      setReportOpen(false);
      setModerationStep("reason");
      return;
    }

    setReportSending(true);

    const moderationActionId = getOrCreateModerationActionId();
    const reportedUid = otherUser.id;
    const reasonCode = moderationReasonCode;
    const trimmedDetails = moderationDetails.trim();
    const detailsToSend = trimmedDetails
      ? trimmedDetails.slice(0, MODERATION_DETAILS_MAX_LENGTH)
      : null;

    let serverConfirmed = false;
    let blockApplied = false;

    try {
      const functions = getFunctions(undefined, "us-central1");
      const reportAndBlockUserFn = httpsCallable(functions, "reportAndBlockUser");

      try {
        const res = await reportAndBlockUserFn({
          reportedUid,
          reason: reasonCode,
          details: detailsToSend,
          callId,
          moderationActionId,
          platform: "web",
        });
        serverConfirmed = Boolean(res?.data?.ok);
        blockApplied = Boolean(res?.data?.blockApplied);
      } catch (e) {
        console.error("reportAndBlockUser error:", e);
        serverConfirmed = false;
        blockApplied = false;
      }

      setReportOpen(false);
      setModerationStep("reason");
      setModerationReasonCode("");
      setModerationDetails("");

      if (serverConfirmed && blockApplied) {
        showToast(
          "success",
          t("reports.submit_success", {
            defaultValue:
              "Report received. Thank you for helping keep Livora safe.",
          })
        );
      } else {
        // Honest partial failure: never claim the other user was blocked
        // unless the server actually confirmed it. The call still ends
        // immediately below for safety either way, and retrying later
        // reuses the same moderationActionId, so it can never create a
        // duplicate report.
        showToast(
          "error",
          t("reports.submit_partial_failure", {
            defaultValue:
              "The call has ended, but we couldn't confirm your report/block. Please try again from settings.",
          })
        );
      }
    } finally {
      setReportSending(false);
      // Always ends the call exactly once, through the same guarded path
      // every other hangup uses - regardless of the outcome above.
      requestHangup("moderation_report_block", "reported_and_blocked");
    }
  }, [
    moderationReasonCode,
    moderationDetails,
    otherUser?.id,
    callId,
    reportSending,
    requestHangup,
    showToast,
    t,
  ]);

const handleSendGift = useCallback(
  async (gift) => {
    if (!gift || !otherUser?.id || !callId || !me) return;
    // Fail-closed guard: only a normalized "male" role may ever reach the
    // addGift callable from this component - independent of GiftStorePage's
    // own disabled/view-only rendering, so a future rendering bug can never
    // by itself let a female/unknown-role session send a gift.
    if (role !== "male") return;

    try {
      setSendingGiftId(gift.id);

      const functions = getFunctions(undefined, "us-central1");
      const addGiftFn = httpsCallable(functions, "addGift");

      const preloadPromise =
        String(gift.assetType || "").toLowerCase() === "svga"
          ? preloadFeaturedGiftSvga(gift.assetUrl)
          : Promise.resolve(null);
      void preloadPromise;

      const res = await addGiftFn({
        toUid: otherUser.id,
        giftId: String(gift.id || ""),
        callId,
        contextType: "call",
      });

      if (res?.data?.ok) {
        const localGift = {
          id: String(gift.id || ""),
          name: String(gift.name || t("calls.gift", { defaultValue: "Gift" })),
          icon: gift.icon || "🎁",
          size: String(gift.size || "small").toLowerCase(),
          sender: String(me?.displayName || me?.name || me?.username || t("calls.you", { defaultValue: "You" })),
          assetType: gift.assetType || "svga",
          assetUrl: gift.assetUrl || "",
          previewUrl: gift.previewUrl || "",
          soundUrl: gift.soundUrl || "",
          display: gift.display || null,
          animation: gift.animation || "",
          offset: gift.offset || null,
          isReceiver: false,
        };

        const eventId = String(res?.data?.giftEventId || "").trim();
        if (eventId) seenGiftEventIdsRef.current.add(eventId);

        pushLargeGiftToStage(localGift);
      }
    } catch (e) {
      console.error("SEND GIFT ERROR OBJECT", e);
      console.error("SEND GIFT ERROR CODE", e?.code);
      console.error("SEND GIFT ERROR MESSAGE", e?.message);
      console.error("SEND GIFT ERROR DETAILS", e?.details);
    } finally {
      setSendingGiftId((current) => (current === gift.id ? "" : current));
    }
  },
  [otherUser?.id, callId, me, role, pushLargeGiftToStage, t]
);

  // ===============================================================
  // LOAD CALL + ME FAST
  // ===============================================================
  useEffect(() => {
    let cancelled = false;

    const loadFast = async () => {
      callDebugLog("[roomPerf] loadFast start", {
        callId,
        ms: Date.now() - roomEnterAtRef.current,
      });
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) {
          callDebugLog("[callDebug] navigate away", {
            from: "loadFast:noAuth",
            callId,
          });
          navigate("/", { replace: true });
          return;
        }

        const [callSnap, meSnap] = await Promise.all([
          getDoc(doc(db, "calls", callId)),
          getDoc(doc(db, "users", currentUser.uid)),
        ]);

        if (!callSnap.exists()) {
          callDebugLog("[callDebug] navigate away", {
            from: "loadFast:callNotFound",
            callId,
            currentUid: currentUser.uid,
          });
          guardedCallNavigate(returnTo, { replace: true }, "loadFast:callNotFound");
          return;
        }

        const call = callSnap.data();

        let myRole = null;
        let otherUid = null;

        if (call.maleUid === currentUser.uid) {
          myRole = "male";
          otherUid = call.femaleUid;
        } else if (call.femaleUid === currentUser.uid) {
          myRole = "female";
          otherUid = call.maleUid;
        } else {
          callDebugLog("[callDebug] navigate away", {
            from: "loadFast:notParticipant",
            callId,
            currentUid: currentUser.uid,
            maleUid: call.maleUid,
            femaleUid: call.femaleUid,
            status: call.status,
          });
          guardedCallNavigate(returnTo, { replace: true }, "loadFast:notParticipant");
          return;
        }

        if (cancelled) return;

        callDebugLog("[callDebug] loadFast ok", {
          callId,
          currentUid: currentUser.uid,
          role: myRole,
          status: call.status,
          maleUid: call.maleUid,
          femaleUid: call.femaleUid,
        });

        setMe(
          meSnap.exists()
            ? { id: currentUser.uid, ...meSnap.data() }
            : { id: currentUser.uid }
        );

        setRole(myRole);
        setCallData({ id: callId, ...call, __uiSignature: getCallDocUiSignature(call) });
        callDocUiSignatureRef.current = getCallDocUiSignature(call);
        callDebugLog("[callPerf] role detected", {
          role: myRole,
          callId,
          elapsed: Math.round(performance.now() - callPerfT0Ref.current),
          status: call.status,
        });
        setLoading(false);
        setCallRoomStage(call.status === "ongoing" ? "connecting" : "loading");
        callDebugLog("[roomPerf] loadFast done", {
          callId,
          ms: Date.now() - roomEnterAtRef.current,
          status: call.status,
        });

        // load other profile in background, do not block room start
        getDoc(doc(db, "publicProfiles", otherUid))
          .then((otherSnap) => {
            if (cancelled) return;
            setOtherUser(
              otherSnap.exists() ? { id: otherUid, ...otherSnap.data() } : { id: otherUid }
            );
          })
          .catch((e) => {
            console.warn("otherUser load warning:", e);
            if (!cancelled) setOtherUser({ id: otherUid });
          });
      } catch (e) {
        console.error("CallRoom loadFast error:", e);
        callDebugLog("[callDebug] navigate away", {
          from: "loadFast:error",
          callId,
          error: e?.message || e,
        });
        guardedCallNavigate(returnTo, { replace: true }, "loadFast:error");
      }
    };

    loadFast();

    return () => {
      cancelled = true;
    };
  }, [callId, guardedCallNavigate, returnTo]);

  // ===============================================================
  // LIVE CALL DOC
  // ===============================================================
  useEffect(() => {
    const ref = doc(db, "calls", callId);
    const platform = "web";
    console.log(
      `[hangupSync] call snapshot listener attached callId=${callId} platform=${platform}`
    );

    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          if (
            shouldBlockCallRoomExit({
              source: "onSnapshot:docMissing",
              callStatus: callDataRef.current?.status || "",
              webStartupInProgress: webStartupInProgressRef.current,
              hasWebStartupSession: !!getActiveWebCallSessionToken(callId),
            })
          ) {
            logBlockedCallExit({ callId, source: "onSnapshot:docMissing" });
            return;
          }
          exitCallRoom({
            source: "onSnapshot:docMissing",
            invokeEndCall: false,
          });
          return;
        }

        const data = snap.data();
        const status = String(data?.status || "");
        if (status && hangupSyncStatusLogRef.current !== status) {
          hangupSyncStatusLogRef.current = status;
          console.log(
            `[hangupSync] call snapshot listener active status=${status} callId=${callId}`
          );
        }

        if (isTerminalCallStatus(data)) {
          const terminalSource = `onSnapshot:terminal:${data.status || "endedAt"}`;
          console.log(
            `[hangupSync] terminal snapshot received callId=${callId} status=${data.status} currentUid=${auth.currentUser?.uid || ""}`
          );
          console.log(`[hangupSync] call snapshot terminal handled callId=${callId}`);
          shutdownAuditStep(
            createShutdownId(callId, terminalSource),
            "firestore_terminal_status_observed",
            true,
            { status: data.status, endedReason: data.endedReason || null }
          );
          exitCallRoom({
            source: terminalSource,
            invokeEndCall: false,
            endedReason: data.endedReason || data.status || null,
            terminalData: data,
          });
          return;
        }

        setCallData((prev) => {
          const merged = mergeCallDocSnapshot(prev, callId, data);
          callDocUiSignatureRef.current = merged.__uiSignature || "";
          return merged;
        });

        const snapshotLogSig = `${callId}|${data.status || ""}|${data.endedReason || ""}|${data.endedAt || ""}`;
        if (callSnapshotLogSigRef.current !== snapshotLogSig) {
          callSnapshotLogSigRef.current = snapshotLogSig;
          callDebugLog("[callDebug] onSnapshot", {
            callId,
            status: data.status,
            endedReason: data.endedReason,
            currentUid: auth.currentUser?.uid,
          });
        }

        if (data.status === "ongoing") {
          setCallRoomStage((prev) =>
            prev === "loading" ? "connecting" : prev
          );
        }
      },
      (err) => {
        console.error("CallRoom snapshot error:", err);
        if (
          shouldBlockCallRoomExit({
            source: "onSnapshot:error",
            callStatus: callDataRef.current?.status || "",
            webStartupInProgress: webStartupInProgressRef.current,
            hasWebStartupSession: !!getActiveWebCallSessionToken(callId),
          })
        ) {
          logBlockedCallExit({ callId, source: "onSnapshot:error" });
          return;
        }
        exitCallRoom({
          source: "onSnapshot:error",
          invokeEndCall: false,
        });
      }
    );

    callDocUnsubRef.current = unsub;

    return () => {
      if (callDocUnsubRef.current) {
        callDocUnsubRef.current();
        callDocUnsubRef.current = null;
      }
    };
  }, [callId, exitCallRoom]);

  useEffect(() => {
    if (!callId || !giftStoreOpen || !secondaryUiReady) return;
    if (giftWarmPreloadStartedRef.current) return;

    giftWarmPreloadStartedRef.current = true;

    const timeoutId = setTimeout(() => {
      const urls = collectWarmPreloadSvgaUrls();

      callDebugLog("[giftPreload] warm start", { count: urls.length });

      if (!urls.length) {
        callDebugLog("[giftPreload] warm done", { count: 0 });
        return;
      }

      void Promise.all(
        urls.map((assetUrl) => {
          callDebugLog("[giftPreload] warm item", { url: assetUrl });
          return preloadFeaturedGiftSvga(assetUrl).catch((error) => {
            callDebugLog("[giftPreload] warm failed", {
              url: assetUrl,
              error: error?.message || error,
            });
            return null;
          });
        })
      ).then(() => {
        callDebugLog("[giftPreload] warm done", { total: urls.length });
      });
    }, GIFT_WARM_PRELOAD_DELAY_MS);

    return () => clearTimeout(timeoutId);
  }, [callId, giftStoreOpen, secondaryUiReady]);

  // ===============================================================
  // GIFTS
  // ===============================================================
  useEffect(() => {
    if (!callId) return;

    const giftEventsQ = query(
      collection(db, "calls", callId, "giftEvents"),
      orderBy("createdAt", "desc"),
      limit(30)
    );

    const unsub = onSnapshot(
      giftEventsQ,
      (snap) => {
        snap.docChanges().forEach((change) => {
          if (change.type !== "added") return;

          const giftDoc = change.doc;
          const eventId = giftDoc.id;
          if (seenGiftEventIdsRef.current.has(eventId)) return;
          seenGiftEventIdsRef.current.add(eventId);

          const data = giftDoc.data() || {};
          const catalogGift = GIFTS.find((g) => g.id === data.giftId);
          const rawSize = String(data.giftSize || catalogGift?.size || "small").toLowerCase();

          const normalizedGift = {
            id: String(data.giftId || eventId),
            name: String(data.giftName || catalogGift?.name || t("calls.gift", { defaultValue: "Gift" })),
            icon: data.giftIcon || catalogGift?.icon || "🎁",
            size: rawSize,
            sender: String(data.senderName || data?.senderSnapshot?.displayName || t("calls.unknown_user", { defaultValue: "User" })),
            assetType: catalogGift?.assetType || "svga",
            assetUrl: catalogGift?.assetUrl || "",
            previewUrl: catalogGift?.previewUrl || "",
            soundUrl: catalogGift?.soundUrl || "",
            display: catalogGift?.display || null,
            animation: catalogGift?.animation || "",
            senderUid: String(data.senderUid || data.fromUid || data.userId || data.uid || ""),
            isReceiver:
              String(data.senderUid || data.fromUid || data.userId || data.uid || "") !==
              String(me?.id || ""),
            offset: catalogGift?.offset || null,
          };

          if (String(catalogGift?.assetType || "").toLowerCase() === "svga") {
            preloadFeaturedGiftSvga(catalogGift.assetUrl);
          }

          pushLargeGiftToStage(normalizedGift);
        });
      },
      (err) => {
        console.error("giftEvents listener error:", err);
      }
    );

    return () => unsub();
  }, [callId, me?.id, pushLargeGiftToStage, t]);

  // ===============================================================
  // FAST ROOM START
  // ===============================================================
  useEffect(() => {
    if (!callId || !me?.id || !role || !webStartGate) return;
    if (engineStartedRef.current) return;

    if (
      exitTriggeredRef.current ||
      hasExitTriggered(callId) ||
      isShutdownInProgress(callId)
    ) {
      console.log(
        `[webZego] startRoom blocked because shutdown/terminal callId=${callId}`
      );
      return;
    }

    const callStatus = String(callDataRef.current?.status || "").toLowerCase();
    if (isTerminalCallStatus({ status: callStatus })) {
      console.log(
        `[webZego] startRoom blocked because shutdown/terminal callId=${callId}`
      );
      return;
    }

    if (!videoContainersReady) return;

    let cancelled = false;
    engineStartedRef.current = true;

    const startRoom = async () => {
      const roomPerfT0 = callPerfT0Ref.current;
      const meSnapshot = meRef.current || { id: me?.id };
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) throw new Error("No auth user");

        logCallPerf("startRoom begin", roomPerfT0, { role, callId });

        callDebugLog("[callDebug] before zego start", {
          callId,
          role,
          currentUid: currentUser.uid,
          status: callDataRef.current?.status,
        });

        console.log(`[webZego] startup begin callId=${callId}`);
        console.log(`[webZego] before token callId=${callId}`);

        let bootstrap = zegoBootstrapRef.current;
        const hasValidBootstrap =
          bootstrap &&
          String(bootstrap.roomId || "") === String(callId) &&
          String(bootstrap.token || "").length > 0 &&
          Number(bootstrap.appID || 0) > 0;

        if (hasValidBootstrap) {
          logCallPerf("bootstrap token ready", roomPerfT0, { role, callId });
          logFirstPaintPerf("token ready", roomEnterAtRef.current, {
            callId,
            source: "bootstrap",
          });
          setCallRoomStage("connecting");
        } else {
          setCallRoomStage("token");
          const tokenFetchT0 = performance.now();
          logCallPerf("fallback token fetch start", roomPerfT0, { role, callId });
          callDebugLog("[callRoom] bootstrap missing, fetching token", { callId });
          const tokenStart = Date.now();
          const functions = getFunctions(undefined, "us-central1");
          const generateZegoKitToken = httpsCallable(
            functions,
            "generateZegoKitToken"
          );

          const tokenRes = await generateZegoKitToken({
            roomId: callId,
            userName:
              meSnapshot.name || meSnapshot.displayName || meSnapshot.username || "User",
          });

          bootstrap = tokenRes?.data || {};
          logCallPerf("fallback token fetch end", tokenFetchT0, { role, callId });
          logFirstPaintPerf("token ready", roomEnterAtRef.current, {
            callId,
            source: "fetch",
            ms: Date.now() - tokenStart,
          });
          setCallRoomStage("connecting");
        }

        console.log(`[webZego] token ready callId=${callId}`);

        const appID = Number(bootstrap.appID || 0);
        const token = String(bootstrap.token || "");
        const userID = String(bootstrap.userID || meSnapshot.id || currentUser.uid || "");
        const userName = String(
          bootstrap.userName ||
            meSnapshot.name ||
            meSnapshot.displayName ||
            meSnapshot.username ||
            "User"
        );
        const roomID = String(bootstrap.roomId || callId || "");
        const localStreamID = `${roomID}_${userID}_main`;

        if (!appID || !token || !userID || !roomID) {
          throw new Error("Invalid ZEGO bootstrap data");
        }

        if (cancelled) return;

        if (
          exitTriggeredRef.current ||
          hasExitTriggered(callId) ||
          isShutdownInProgress(callId)
        ) {
          console.log(
            `[webZego] startRoom blocked because shutdown/terminal callId=${callId}`
          );
          engineStartedRef.current = false;
          return;
        }

        const liveStatus = String(callDataRef.current?.status || "").toLowerCase();
        if (isTerminalCallStatus({ status: liveStatus }) || liveStatus !== "ongoing") {
          console.log(
            `[webZego] startRoom blocked because call not ongoing callId=${callId} status=${liveStatus}`
          );
          engineStartedRef.current = false;
          return;
        }

        const platformMode = getCallPlatformMode();
        console.log(
          "[callPlatform] startRoom decision",
          platformMode.useNativeCall && platformMode.nativeReady ? "native" : "web"
        );
        console.log("[callPlatform] startRoom reason", platformMode.reason);

        if (platformMode.useNativeCall && platformMode.nativeReady) {
          console.log(
            "[callPlatform] startRoom blocked native path not wired in CallRoomPage"
          );
          engineStartedRef.current = false;
          return;
        }

        if (!platformMode.useWebCall) {
          console.log("[callPlatform] startRoom blocked web path disabled by platform gate");
          engineStartedRef.current = false;
          return;
        }

        logStreamIdCheck({
          roomId: roomID,
          localUserId: userID,
          localStreamId: localStreamID,
        });

        transitionCallLifecycle(callId, "web_zego_starting", { role, platformMode: "web" });

        try {
          logSnapCallFilterProbe();
        } catch {
          // Snap probe is optional and must never block Web Zego startup.
        }

        console.log(`[webZego] startup allowed platform=${platformMode.platform}`);
        console.log(
          `[webZego] remote container exists=${!!remoteVideoRef.current} local container exists=${!!localVideoRef.current}`
        );

        webStartupInProgressRef.current = true;
        const sessionToken = beginWebCallStartup(callId, callDataRef.current?.status);
        if (!sessionToken) {
          webStartupInProgressRef.current = false;
          engineStartedRef.current = false;
          return;
        }
        webSessionTokenRef.current = sessionToken;
        startRoomEffectSessionRef.current = sessionToken;
        console.log(
          `[webZego] startup session active callId=${callId} session=${sessionToken} activeSession=${getActiveWebCallSessionToken(callId) || "none"}`
        );

        if (
          cancelWebCallStartupIfBlocked(callId, sessionToken, "token_fetch")
        ) {
          webStartupInProgressRef.current = false;
          engineStartedRef.current = false;
          return;
        }

        if (cancelled) {
          console.log(`[webZego] startup cancelled after token_fetch cancelled=true callId=${callId}`);
          webStartupInProgressRef.current = false;
          engineStartedRef.current = false;
          return;
        }

        await unlockWebCallAudio();
        if (
          cancelWebCallStartupIfBlocked(callId, sessionToken, "unlockWebCallAudio")
        ) {
          webStartupInProgressRef.current = false;
          engineStartedRef.current = false;
          return;
        }

        if (cancelled) {
          console.log(
            `[webZego] startup cancelled after unlockWebCallAudio cancelled=true callId=${callId}`
          );
          webStartupInProgressRef.current = false;
          engineStartedRef.current = false;
          return;
        }

        console.log(`[webZego] before createEngine callId=${callId}`);
        const engineT0 = performance.now();
        logCallPerf("Zego engine create start", engineT0, { role, callId });
        callDebugLog("[roomPerf] before zego init", {
          callId,
          ms: Date.now() - roomEnterAtRef.current,
        });
        const zg = new ZegoExpressEngine(appID, undefined);
        registerWebCallMediaEngine(callId, zg, roomID, sessionToken);
        zegoRef.current = zg;
        console.log(`[webZego] engine created callId=${callId} session=${sessionToken}`);
        logCallPerf("Zego engine created", engineT0, { role, callId });
        logFirstPaintPerf("Zego engine created", roomEnterAtRef.current, { callId });

        if (
          cancelWebCallStartupIfBlocked(callId, sessionToken, "engine_create", { zegoEngine: zg })
        ) {
          webStartupInProgressRef.current = false;
          zegoRef.current = null;
          engineStartedRef.current = false;
          return;
        }

        try {
          if (zg.enableAEC) await zg.enableAEC(true);
          if (zg.enableAGC) await zg.enableAGC(true);
          if (zg.enableANS) await zg.enableANS(true);
        } catch (e) {
          console.warn("ZEGO audio preprocess warning:", e);
        }

        if (
          cancelWebCallStartupIfBlocked(callId, sessionToken, "engine_preprocess", {
            zegoEngine: zg,
          })
        ) {
          webStartupInProgressRef.current = false;
          zegoRef.current = null;
          engineStartedRef.current = false;
          return;
        }

        try {
          if (zg.startSoundLevelMonitor) {
            zg.startSoundLevelMonitor(500);
          }
        } catch (e) {
          console.warn("startSoundLevelMonitor warning:", e);
        }

        zg.on("capturedSoundLevelUpdate", (level) => {
          if (role === "male" && Number(level) > 3) {
            callDebugLog("[audioFix] male local sound level", {
              role,
              callId,
              level,
            });
          }
        });

        zg.on("remoteSoundLevelUpdate", (levels) => {
          if (role !== "female" || !levels) return;
          const entries = Array.isArray(levels)
            ? levels.map((item, index) => [String(index), item])
            : Object.entries(levels);
          entries.forEach(([streamID, level]) => {
            if (Number(level) > 3) {
              callDebugLog("[audioFix] female remote sound level", {
                role,
                callId,
                streamID,
                level,
              });
            }
          });
        });

        zg.on("audioRouteChange", (route) => {
          callDebugLog("Audio route changed:", route);
        });

        zg.on("roomStateChanged", (roomIDArg, reason, errorCode) => {
          callDebugLog("roomStateChanged:", roomIDArg, reason, errorCode);

          const reasonStr = String(reason || "");
          if (
            reasonStr.includes("Logined") ||
            reasonStr.includes("Reconnected") ||
            reasonStr === "LOGINED" ||
            reasonStr === "RECONNECTED"
          ) {
            setRoomReady((prev) => (prev ? prev : true));
            if (!firstPaintMarkersRef.current.roomReady) {
              firstPaintMarkersRef.current.roomReady = true;
              logFirstPaintPerf("room ready", roomEnterAtRef.current, {
                callId,
                reason: reasonStr,
              });
              scheduleSecondaryUi("roomStateChanged");
            }
          }
        });

        zg.on("roomStreamUpdate", async (_roomIDArg, updateType, streamList) => {
          if (
            !isWebCallStartupAllowed(callId, sessionToken, "remote_stream_callback")
          ) {
            return;
          }

          (streamList || []).forEach((item) => {
            console.log(
              `[webZego] room stream update type=${updateType} streamID=${item?.streamID || ""}`
            );
          });

          if (updateType === "ADD") {
            const remoteStreamInfo = streamList.find(
              (s) => s.streamID !== publishedStreamIDRef.current
            );
            if (!remoteStreamInfo) return;

            setRemoteConnected(true);

            const remoteAddT0 = performance.now();
            logCallPerf("first remote stream ADD", remoteAddT0, {
              role,
              callId,
              streamID: remoteStreamInfo.streamID,
            });

            const remoteUserId = parseRemoteUserIdFromStreamId(
              remoteStreamInfo.streamID,
              roomID
            );
            logStreamIdCheck({
              roomId: roomID,
              localUserId: userID,
              localStreamId: localStreamID,
              remoteStreamId: remoteStreamInfo.streamID,
              remoteUserId,
            });

            try {
              if (
                playedStreamIDRef.current &&
                playedStreamIDRef.current !== remoteStreamInfo.streamID
              ) {
                try {
                  zg.stopPlayingStream(playedStreamIDRef.current);
                } catch (_) {}
              }

              const remotePlayT0 = performance.now();
              logCallPerf("remote play start", remotePlayT0, {
                role,
                callId,
                streamID: remoteStreamInfo.streamID,
              });

              console.log(
                `[webZego] startPlayingStream called streamID=${remoteStreamInfo.streamID}`
              );
              const remoteStream = await zg.startPlayingStream(remoteStreamInfo.streamID, {
                video: true,
                audio: true,
              });

              if (
                cancelWebCallStartupIfBlocked(callId, sessionToken, "startPlayingStream", {
                  remoteStream,
                  zegoEngine: zg,
                })
              ) {
                try {
                  zg.stopPlayingStream(remoteStreamInfo.streamID);
                } catch (_) {}
                return;
              }

              remoteStreamRef.current = remoteStream;
              playedStreamIDRef.current = remoteStreamInfo.streamID;

              const remoteVideoTracks = remoteStream?.getVideoTracks?.() || [];
              const remoteAudioTracks = remoteStream?.getAudioTracks?.() || [];
              console.log(
                `[webZego] remote videoTracks count=${remoteVideoTracks.length}`
              );
              console.log(
                `[webZego] remote audioTracks count=${remoteAudioTracks.length}`
              );

              registerWebCallMediaRemoteStream(
                callId,
                remoteStreamInfo.streamID,
                remoteStream,
                sessionToken
              );

              if (remoteViewRef.current?.stop) {
                remoteViewRef.current.stop();
              }
              remoteViewRef.current = null;

              const remoteContainer = remoteVideoRef.current;
              await attachStreamToCallVideoElement({
                container: remoteContainer,
                stream: remoteStream,
                streamID: remoteStreamInfo.streamID,
                scope: "remote",
              });
              console.log(
                `[webZego] remote video attached streamID=${remoteStreamInfo.streamID}`
              );
              setRemoteVideoAttached(true);

              registerWebCallMediaElementsFromContainer(
                callId,
                remoteStreamInfo.streamID,
                remoteContainer,
                "remote",
                sessionToken
              );

              if (
                cancelWebCallStartupIfBlocked(callId, sessionToken, "remote_video_attach", {
                  remoteStream,
                  zegoEngine: zg,
                  container: remoteContainer,
                })
              ) {
                return;
              }

              ensureWebPlayAudioUnmuted(zg, remoteStreamInfo.streamID);
              logWebRemoteAudioTracks(remoteStream, callId);
              logWebRemoteAudioAttached(remoteStreamInfo.streamID, callId, remoteStream);

              logCallPerf("remote play success", remotePlayT0, {
                role,
                callId,
                streamID: remoteStreamInfo.streamID,
              });

              // This is the genuine Web-runtime equivalent of Native's Zego
              // PLAYING callback - the remote MediaStream has actually been
              // obtained and attached, not merely requested. Male-only (the
              // paying participant), never fired from route mount or accept
              // status.
              if (role === "male") {
                void sendWebMediaConnectedBilling(callId);
              }

              setCallRoomStage((prev) => (prev === "ready" ? prev : "ready"));

              if (!firstPaintMarkersRef.current.remoteVideo) {
                firstPaintMarkersRef.current.remoteVideo = true;
                logFirstPaintPerf("first remote video render", roomEnterAtRef.current, {
                  callId,
                  streamID: remoteStreamInfo.streamID,
                });
              }
              scheduleSecondaryUi("remoteConnected");
            } catch (e) {
              console.error("startPlayingStream error:", e);
              console.log(
                `[webZego] remote video play failed streamID=${remoteStreamInfo.streamID} error=${e?.message || e}`
              );
              logCallPerf("remote play fail", remoteAddT0, {
                role,
                callId,
                error: e?.message || e,
              });
            }
          }

          if (updateType === "DELETE") {
            const deletedMine = streamList.some(
              (s) => s.streamID === playedStreamIDRef.current
            );

            if (deletedMine) {
              try {
                if (remoteViewRef.current?.stop) remoteViewRef.current.stop();
                if (playedStreamIDRef.current) {
                  zg.stopPlayingStream(playedStreamIDRef.current);
                }
              } catch (e) {
                console.warn("stop remote play warning:", e);
              }

              remoteViewRef.current = null;
              remoteStreamRef.current = null;
              playedStreamIDRef.current = null;
              setRemoteVideoAttached(false);
              setRemoteConnected((prev) => (prev ? false : prev));
            }
          }
        });

        callDebugLog("[roomPerf] after zego init", {
          callId,
          ms: Date.now() - roomEnterAtRef.current,
        });

        setCallRoomStage("connecting");
        console.log(`[webZego] before loginRoom callId=${callId} roomID=${roomID}`);
        const loginT0 = performance.now();
        logCallPerf("loginRoom start", loginT0, { role, callId });
        logFirstPaintPerf("loginRoom start", roomEnterAtRef.current, { callId });
        const loginStart = Date.now();
        callDebugLog("[roomPerf] before loginRoom", {
          callId,
          ms: Date.now() - roomEnterAtRef.current,
        });
        const loginOk = await zg.loginRoom(
          roomID,
          token,
          { userID, userName },
          { userUpdate: true }
        );
        logCallPerf("loginRoom end", loginT0, { role, callId, loginOk: !!loginOk });
        callDebugLog("[callRoom] loginRoomMs", {
          callId,
          ms: Date.now() - loginStart,
        });
        callDebugLog("[roomPerf] after loginRoom", {
          callId,
          ms: Date.now() - roomEnterAtRef.current,
        });

        if (!loginOk) {
          throw new Error("ZEGO loginRoom failed");
        }

        console.log(`[webZego] loginRoom success callId=${callId} roomID=${roomID}`);

        if (
          cancelWebCallStartupIfBlocked(callId, sessionToken, "loginRoom", { zegoEngine: zg })
        ) {
          webStartupInProgressRef.current = false;
          zegoRef.current = null;
          engineStartedRef.current = false;
          return;
        }

        logFirstPaintPerf("loginRoom end", roomEnterAtRef.current, {
          callId,
          loginOk: true,
        });

        if (cancelled) return;

        setRoomReady(true);
        scheduleSecondaryUi("loginRoom");

        publishedStreamIDRef.current = localStreamID;

        setCallRoomStage("camera");
        const cameraStart = Date.now();
        logFirstPaintPerf("createZegoStream start", roomEnterAtRef.current, { callId });

        callDebugLog("[roomPerf] before createZegoStream", {
          callId,
          ms: Date.now() - roomEnterAtRef.current,
        });
        console.log(`[webZego] before createLocalStream callId=${callId}`);
        let { localStream, hasAudio, micEnabled } = await createValidatedLocalStream(
          zg,
          role,
          "user",
          callId
        );

        if (
          cancelWebCallStartupIfBlocked(callId, sessionToken, "createStream", {
            localStream,
            zegoEngine: zg,
          })
        ) {
          webStartupInProgressRef.current = false;
          zegoRef.current = null;
          engineStartedRef.current = false;
          return;
        }

        ({ localStream, hasAudio, micEnabled } = await prepareMaleStreamForPublish(
          zg,
          localStream,
          hasAudio,
          micEnabled,
          role,
          callId,
          "user"
        ));

        if (
          cancelWebCallStartupIfBlocked(callId, sessionToken, "prepareMaleStream", {
            localStream,
            zegoEngine: zg,
          })
        ) {
          webStartupInProgressRef.current = false;
          zegoRef.current = null;
          engineStartedRef.current = false;
          return;
        }

        localStreamRef.current = localStream;
        registerWebCallMediaLocalStream(callId, localStream, localStreamID, sessionToken);
        setMicOn(hasAudio ? micEnabled : false);

        const localPreviewT0 = performance.now();
        logCallPerf("local video preview play start", localPreviewT0, { role, callId });
        console.log(`[webZego] before attach local preview callId=${callId}`);
        const localAttachResult = await attachStreamToCallVideoElement({
          container: localVideoRef.current,
          stream: localStream,
          streamID: localStreamID,
          scope: "local",
          mirror: true,
        });
        localPreviewStreamRef.current = localAttachResult.mediaStream || null;
        if (localAttachResult.ok) {
          console.log(`[webZego] local preview attached callId=${callId}`);
          setLocalVideoAttached(true);
        } else {
          console.log(`[webZego] local preview attach skipped callId=${callId}`);
        }
        registerWebCallMediaElementsFromContainer(
          callId,
          localStreamID,
          localVideoRef.current,
          "local",
          sessionToken
        );

        if (
          cancelWebCallStartupIfBlocked(callId, sessionToken, "local_video_attach", {
            localStream,
            zegoEngine: zg,
            container: localVideoRef.current,
          })
        ) {
          webStartupInProgressRef.current = false;
          zegoRef.current = null;
          engineStartedRef.current = false;
          return;
        }
        logCallPerf("local video preview play success", localPreviewT0, { role, callId });
        if (!firstPaintMarkersRef.current.localVideo) {
          firstPaintMarkersRef.current.localVideo = true;
          logFirstPaintPerf("first local video render", roomEnterAtRef.current, {
            callId,
          });
        }
        logFirstPaintPerf("createZegoStream end", roomEnterAtRef.current, { callId });
        callDebugLog("[roomPerf] after createZegoStream", {
          callId,
          ms: Date.now() - roomEnterAtRef.current,
        });

        const cameraMs = Date.now() - cameraStart;
        const totalToLocalVideoMs = Date.now() - roomEnterAtRef.current;
        callDebugLog("[callRoom] cameraMs", { callId, ms: cameraMs });
        callDebugLog("[callRoom] localVideoReadyMs", {
          callId,
          ms: totalToLocalVideoMs,
        });
        callDebugLog("[callRoom] totalToLocalVideoMs", {
          callId,
          ms: totalToLocalVideoMs,
        });

        usingFrontCameraRef.current = true;
        logWebLocalMicTrack(localStream, callId, role);
        ensureWebPublishAudioUnmuted(zg);

        const publishT0 = performance.now();
        logCallPerf("startPublishingStream start", publishT0, { role, callId, hasAudio });
        callDebugLog("[roomPerf] before startPublishingStream", {
          callId,
          ms: Date.now() - roomEnterAtRef.current,
        });
        console.log(
          `[webZego] before startPublishingStream streamID=${localStreamID} callId=${callId}`
        );
        if (hasAudio) {
          callDebugLog("[audioFix] publish with audio track", { role, callId });
        } else {
          console.error("[audioFix] publish without audio track", { role, callId });
        }
        zg.startPublishingStream(localStreamID, localStream);
        console.log(
          `[webZego] startPublishingStream called streamID=${localStreamID} callId=${callId}`
        );
        logCallPerf("startPublishingStream called", publishT0, { role, callId, hasAudio });

        if (
          cancelWebCallStartupIfBlocked(callId, sessionToken, "startPublishingStream", {
            localStream,
            zegoEngine: zg,
          })
        ) {
          webStartupInProgressRef.current = false;
          zegoRef.current = null;
          engineStartedRef.current = false;
          return;
        }

        markWebCallActive(callId, sessionToken);
        webStartupInProgressRef.current = false;
        try {
          void runSnapCameraKitInitProbe({ callId });
        } catch {
          // Init probe must never block Web Zego startup.
        }
        try {
          void runSnapLensLoadProbe({ callId });
        } catch {
          // Lens load probe must never block Web Zego startup.
        }
        try {
          void runSnapRealApplyProbe({
            callId,
            zegoEngine: zg,
            zegoLocalStream: localStream,
            streamID: localStreamID,
          });
        } catch {
          // Real apply probe must never block Web Zego startup.
        }
        try {
          void runZegoRawPumpProbe({
            callId,
            zegoEngine: zg,
            zegoLocalStream: localStream,
            streamID: localStreamID,
          });
        } catch {
          // Raw pump probe must never block Web Zego startup.
        }
        logFirstPaintPerf("publishStream end", roomEnterAtRef.current, { callId });
        scheduleSecondaryUi("published");
        callDebugLog("[roomPerf] after startPublishingStream", {
          callId,
          ms: Date.now() - roomEnterAtRef.current,
        });
        setCallRoomStage("waiting_remote");
        callDebugLog("[callDebug] zego started", {
          callId,
          role,
          currentUid: currentUser.uid,
        });
      } catch (e) {
        console.error("[callDebug] zego error", {
          callId,
          role,
          currentUid: auth.currentUser?.uid,
          name: e?.name,
          message: e?.message || e,
          isMediaPermission: e?.isMediaPermission === true,
        });
        engineStartedRef.current = false;
        webStartupInProgressRef.current = false;
        if (isMediaPermissionError(e)) {
          setMediaPermissionFailed(true);
          return;
        }
        await cleanupMediaRef.current?.({ endNative: false, reason: "startRoom:zegoError" });
        callDebugLog("[callDebug] navigate away", {
          from: "startRoom:zegoError",
          callId,
          error: e?.message || e,
        });
        navigateDuringWebStartup(returnTo, { replace: true }, "startRoom:zegoError");
      }
    };

    startRoom();

    return () => {
      cancelled = true;
      invalidateSnapCameraKitInitProbe("startRoom_effect_cleanup");
      invalidateSnapLensLoadProbe("startRoom_effect_cleanup");
      invalidateSnapRealApplyProbe("startRoom_effect_cleanup");
      void cleanupSnapRealApplyProbe("startRoom_effect_cleanup");
      invalidateZegoRawPumpProbe("startRoom_effect_cleanup");
      void cleanupZegoRawPumpProbe("startRoom_effect_cleanup");
      noteCallRoomPassiveUnmount(callId);
      const staleSession = startRoomEffectSessionRef.current;
      console.log(
        `[webZego] startRoom effect cleanup callId=${callId} session=${staleSession || "none"}`
      );
      console.log("[webZego] cleanup source stack=", new Error().stack);
      console.log(`[webZego] mountedRef=${mountedRef.current}`);
      console.log(`[webZego] current callId=${callId}`);
      console.log(`[webZego] route pathname=${window.location?.pathname || ""}`);
      if (staleSession) {
        cancelWebCallStartupSession(callId, staleSession, "startRoom_effect_cleanup");
      }
      if (webSessionTokenRef.current === staleSession) {
        webSessionTokenRef.current = getActiveWebCallSessionToken(callId);
      }
      startRoomEffectSessionRef.current = null;
      engineStartedRef.current = false;
    };
  }, [callId, me?.id, role, webStartGate, videoContainersReady, navigateDuringWebStartup]);

  // ===============================================================
  // HEARTBEAT (male billing — Web Zego, server heartbeatCall)
  // ===============================================================
  useEffect(() => {
    if (!callId) return undefined;
    const status = callData?.status || callDataRef.current?.status || "";
    const normalizedStatus = String(status).toLowerCase();

    syncWebCallHeartbeat({
      callId,
      role,
      status: normalizedStatus,
      onServerEnded: (data) => {
        const endedReason = data?.endedReason || "heartbeat_ended";
        exitCallRoom({
          source: "heartbeat:ended",
          invokeEndCall: false,
          endedReason,
          terminalData: { status: "ended", endedReason },
        });
      },
    });
    return undefined;
  }, [callId, role, callData?.status, callData?.maleUid, me?.id, exitCallRoom]);

  useEffect(() => {
    return () => {
      stopWebCallHeartbeat(callId, "call_route_unmount");
      heartbeatRef.current = null;
    };
  }, [callId]);

  // ===============================================================
  // TIMER
  // ===============================================================
  useEffect(() => {
    if (callData?.status !== "ongoing") return;
    if (timerRef.current) return;

    timerRef.current = setInterval(() => {
      setCallSeconds((s) => s + 1);
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [callData?.status]);

  // ===============================================================
  // REBIND VIEWS AFTER SWAP
  // ===============================================================
  useEffect(() => {
    if (!videoContainersReady) return;

    const rebind = async () => {
      try {
        if (localStreamRef.current && localVideoRef.current) {
          const existingVideo = localVideoRef.current.querySelector?.(
            "video[data-livora-zego-video]"
          );
          if (!existingVideo?.srcObject) {
            console.log("[webZego] video container changed, reattaching local stream");
            const previewStream =
              localPreviewStreamRef.current || localStreamRef.current;
            const rebindAttachResult = await attachStreamToCallVideoElement({
              container: localVideoRef.current,
              stream: previewStream,
              streamID: publishedStreamIDRef.current || "local",
              scope: "local",
              mirror: usingFrontCameraRef.current,
            });
            if (rebindAttachResult.mediaStream) {
              localPreviewStreamRef.current = rebindAttachResult.mediaStream;
            }
            if (rebindAttachResult.ok) {
              setLocalVideoAttached(true);
            }
          }
        }

        if (remoteStreamRef.current && remoteVideoRef.current) {
          const existingVideo = remoteVideoRef.current.querySelector?.(
            "video[data-livora-zego-video]"
          );
          if (!existingVideo?.srcObject) {
            console.log("[webZego] video container changed, reattaching remote stream");
            await attachStreamToCallVideoElement({
              container: remoteVideoRef.current,
              stream: remoteStreamRef.current,
              streamID: playedStreamIDRef.current || "remote",
              scope: "remote",
            });
            setRemoteVideoAttached(true);
          }
        }
      } catch (e) {
        console.warn("rebind video views warning:", e);
      }
    };

    void rebind();
  }, [isRemoteMain, videoContainersReady]);

  useEffect(() => {
    return () => {
      Object.values(liveGiftTimersRef.current).forEach((timers) => {
        if (timers?.leaveTimer) clearTimeout(timers.leaveTimer);
        if (timers?.removeTimer) clearTimeout(timers.removeTimer);
      });
      liveGiftTimersRef.current = {};

      if (featuredGiftTimerRef.current) {
        clearTimeout(featuredGiftTimerRef.current);
        featuredGiftTimerRef.current = null;
      }

      if (giftSoundRef.current) {
        try {
          giftSoundRef.current.pause();
          giftSoundRef.current.currentTime = 0;
        } catch (_) {}
        giftSoundRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    return () => {
      console.log(`[webZego] component unmount cleanup triggered callId=${callId}`);
      console.log("[webZego] cleanup source stack=", new Error().stack);
      console.log(`[webZego] mountedRef=${mountedRef.current}`);
      console.log(`[webZego] current callId=${callId}`);
      console.log(`[webZego] route pathname=${window.location?.pathname || ""}`);
      console.log(
        `[webZego] active startup session=${getActiveWebCallSessionToken(callId) || "none"} webStartupInProgress=${webStartupInProgressRef.current}`
      );

      if (mediaCleanedRef.current || mediaCleanupInProgressRef.current) return;

      noteCallRoomPassiveUnmount(callId);

      const traceId = createShutdownId(callId, "component_unmount");
      const callStatus = String(callDataRef.current?.status || "").toLowerCase();
      const callStillActive =
        callStatus === "ongoing" || callStatus === "ringing" || callStatus === "accepted";
      const callStatusUnknown = !callStatus;
      const realShutdown =
        exitTriggeredRef.current ||
        hasExitTriggered(callId) ||
        isShutdownInProgress(callId);

      if ((callStillActive || callStatusUnknown) && !realShutdown) {
        tryClearPassiveTombstoneForActiveCall(
          callId,
          callStillActive ? callStatus : "ongoing"
        );
        console.log(
          `[shutdownAudit:web] component_unmount passive skip tombstone callId=${callId}`
        );
        console.log(`[webMediaRegistry] passive unmount no closed tombstone callId=${callId}`);
        return;
      }

      void cleanupMediaRef.current?.({
        endNative: false,
        reason: "manual_cleanup_after_end",
        shutdownId: traceId,
      });
    };
  }, [callId]);

  if (loading || !callData || !me) {
    return (
      <div className="callroom-root callroom-root--state">
        <div className="callroom-center">
          {getCallRoomStageLabel(callRoomStage === "loading" ? "loading" : callRoomStage)}
        </div>
      </div>
    );
  }

  if (!isActiveCallStatus(callData.status)) {
    return (
      <div className="callroom-root callroom-root--state">
        <div className="callroom-center">
          {t("calls.waiting_start", {
            defaultValue: "بانتظار بدء المكالمة...",
          })}
        </div>
      </div>
    );
  }

  if (mediaPermissionFailed) {
    return (
      <div className="callroom-root callroom-root--state">
        <div
          className="callroom-center"
          style={{
            flexDirection: "column",
            gap: 20,
            padding: 24,
            textAlign: "center",
          }}
        >
          <p style={{ margin: 0, maxWidth: 320, lineHeight: 1.5 }}>
            {t("calls.media_permission_required", {
              defaultValue:
                "Microphone or camera permission is required for this call.",
            })}
          </p>
          <button
            type="button"
            className="callroom-hangup-btn"
            onClick={() =>
              requestHangup("ui:mediaPermission")
            }
          >
            {t("calls.actions.end_call", { defaultValue: "End call" })}
          </button>
        </div>
      </div>
    );
  }

  const remotePlaceholderStage =
    callRoomStage === "token" ||
    callRoomStage === "connecting" ||
    callRoomStage === "camera"
      ? callRoomStage
      : "waiting_remote";

  const showControlsDock = controlsVisible && !chatOpen;
  const isChatMode = chatOpen && secondaryUiReady;

  return (
    <div className={`callroom-root${secondaryUiReady ? "" : " callroom-root--first-paint"}`}>
      <SensitiveContentGuard maskOnBlur={false}>
        <div
          className={`callroom-stage${
            isChatMode ? " callroom-chat-mode" : ""
          }${isSwappingViews ? " is-swapping" : ""}`}
        >
          <div
            ref={isRemoteMain ? remoteVideoRef : localVideoRef}
            className="callroom-main-video"
          />

          {isRemoteMain && !remoteConnected && !remoteVideoAttached && (
            <div className="callroom-stage-placeholder">
              {getCallRoomStageLabel(remotePlaceholderStage)}
            </div>
          )}

          <div
            className={`callroom-swap-soft-mask${
              isSwappingViews ? " active" : ""
            }`}
            aria-hidden="true"
          />

          {!giftStoreOpen && !reportOpen && (
            <div
              className="callroom-screen-touch-layer"
              onClick={handleStageBackgroundTap}
              aria-hidden="true"
            />
          )}

          <div
            ref={isRemoteMain ? localVideoRef : remoteVideoRef}
            className="callroom-pip-video"
            role="button"
            tabIndex={0}
            onClick={handlePipSwap}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handlePipSwap(e);
              }
            }}
            aria-label={
              isRemoteMain
                ? t("calls.self_label", { defaultValue: "(You)" })
                : t("calls.remote_label", { defaultValue: "(Remote)" })
            }
          >
            <span className="callroom-pip-label">
              {isRemoteMain
                ? t("calls.self_label", { defaultValue: "(You)" })
                : t("calls.remote_label", { defaultValue: "(Remote)" })}
            </span>
          </div>

          <div className="callroom-topbar">
            <div className="callroom-timer-pill">{formatTime(callSeconds)}</div>
            {secondaryUiReady && (
              <div className="callroom-options-wrap">
                <button
                  type="button"
                  className="callroom-report-fab"
                  onClick={(e) => {
                    e.stopPropagation();
                    setOptionsMenuOpen((prev) => !prev);
                  }}
                  aria-label={t("calls.report_flag_button_title", { defaultValue: "Report and block user" })}
                  title={t("calls.report_flag_button_title", { defaultValue: "Report and block user" })}
                >
                  <Flag size={18} />
                </button>

                {optionsMenuOpen && (
                  <>
                    <div
                      className="callroom-options-backdrop"
                      onClick={() => setOptionsMenuOpen(false)}
                    />
                    <div className="callroom-options-menu" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        className="callroom-options-menu-item"
                        onClick={() => {
                          setOptionsMenuOpen(false);
                          setModerationStep("reason");
                          setModerationReasonCode("");
                          setModerationDetails("");
                          setReportOpen(true);
                        }}
                      >
                        {t("reports.report_and_block", { defaultValue: "Report and Block" })}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {liveGifts.length > 0 && <LiveGiftsOverlay gifts={liveGifts} />}
          {featuredGift && <FeaturedGiftStage gift={featuredGift} />}

          <div
            className={`callroom-bottom-glass-dock${
              showControlsDock ? "" : " hidden"
            }${secondaryUiReady ? "" : " callroom-bottom-glass-dock--first-paint"}`}
          >
            <div className="callroom-dock-content">
              {secondaryUiReady && (
                <div className="callroom-dock-group callroom-dock-group--left">
                  <button
                    type="button"
                    className={`callroom-control-btn ${giftStoreOpen ? "active" : ""}`}
                    onClick={openGiftStore}
                    title={t("calls.actions.gift", { defaultValue: "Gift" })}
                  >
                    <Gift size={20} />
                  </button>

                  <button
                    type="button"
                    className={`callroom-control-btn ${chatOpen ? "active" : ""}`}
                    onClick={toggleChatOpen}
                    title={t("calls.actions.chat", { defaultValue: "Chat" })}
                  >
                    <MessageCircle size={20} />
                  </button>

                  <button
                    type="button"
                    className={`callroom-control-btn ${flippingCamera ? "busy" : ""}`}
                    onClick={flipCamera}
                    title={t("calls.actions.flip_camera", { defaultValue: "Flip camera" })}
                  >
                    <RefreshCw size={20} />
                  </button>
                </div>
              )}

              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  requestHangup(
                    secondaryUiReady ? "ui:hangupButton" : "ui:firstPaintHangup"
                  );
                }}
                className="callroom-hangup-btn"
                title={t("calls.actions.end", { defaultValue: "End" })}
              >
                <PhoneOff size={22} />
              </button>

              {secondaryUiReady && (
                <div className="callroom-dock-group callroom-dock-group--right">
                  <button
                    type="button"
                    className={`callroom-control-btn ${!cameraOn ? "off" : ""}`}
                    onClick={toggleCamera}
                    title={t("calls.actions.camera_off", { defaultValue: "Turn camera off" })}
                  >
                    {cameraOn ? <Video size={20} /> : <VideoOff size={20} />}
                  </button>

                  <button
                    type="button"
                    className={`callroom-control-btn ${!micOn ? "off" : ""}`}
                    onClick={toggleMic}
                    title={t("calls.actions.mute", { defaultValue: "Mute" })}
                  >
                    {micOn ? <Mic size={20} /> : <MicOff size={20} />}
                  </button>
                </div>
              )}
            </div>
          </div>

          {isChatMode && (
            <div className="callroom-chat-dock">
              <CallChatSheet
                open
                callId={callId}
                me={me}
                otherUser={otherUser}
                onGiftClick={openGiftStore}
              />
            </div>
          )}

          {secondaryUiReady && giftStoreOpen && (
            <div className="callroom-giftstore-layer">
              <GiftStorePage
                open={giftStoreOpen}
                onClose={closeGiftStore}
                onSendGift={handleSendGift}
                sendingGiftId={sendingGiftId}
                viewerRole={role}
                defaultTab="small"
                currentCoins={Number(me?.coins || 0)}
                topupPath="/buy-coins"
              />
            </div>
          )}

          {secondaryUiReady && reportOpen && moderationStep === "reason" && (
            <div
              className="callroom-report-backdrop"
              onClick={() => !reportSending && setReportOpen(false)}
            >
              <div
                className="callroom-report-card"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="callroom-report-title">
                  {t("reports.choose_reason", { defaultValue: "Choose a report reason" })}
                </div>

                <div className="callroom-report-options">
                  {REPORT_REASONS.map((reasonItem) => (
                    <button
                      key={reasonItem.code}
                      type="button"
                      className={`callroom-report-option${
                        moderationReasonCode === reasonItem.code ? " selected" : ""
                      }`}
                      onClick={() => setModerationReasonCode(reasonItem.code)}
                      disabled={reportSending}
                    >
                      {t(reasonItem.labelKey, {
                        defaultValue: reasonItem.defaultValue,
                      })}
                    </button>
                  ))}
                </div>

                <textarea
                  className="callroom-report-details"
                  value={moderationDetails}
                  onChange={(e) =>
                    setModerationDetails(e.target.value.slice(0, MODERATION_DETAILS_MAX_LENGTH))
                  }
                  maxLength={MODERATION_DETAILS_MAX_LENGTH}
                  placeholder={t("reports.details_placeholder", {
                    defaultValue: "Additional details (optional)",
                  })}
                  disabled={reportSending}
                  rows={3}
                />

                <div className="callroom-report-actions">
                  <button
                    type="button"
                    className="callroom-report-cancel"
                    onClick={() => setReportOpen(false)}
                    disabled={reportSending}
                  >
                    {t("common.cancel", { defaultValue: "Cancel" })}
                  </button>
                  <button
                    type="button"
                    className="callroom-report-next"
                    onClick={() => setModerationStep("confirm")}
                    disabled={reportSending || !moderationReasonCode}
                  >
                    {t("common.next", { defaultValue: "Next" })}
                  </button>
                </div>
              </div>
            </div>
          )}

          {secondaryUiReady && reportOpen && moderationStep === "confirm" && (
            <div
              className="callroom-report-backdrop"
              onClick={() => !reportSending && setModerationStep("reason")}
            >
              <div
                className="callroom-report-card"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="callroom-report-title">
                  {t("reports.report_and_block", { defaultValue: "Report and Block" })}
                </div>

                <p className="callroom-report-confirm-text">
                  {t("reports.confirm_message", {
                    defaultValue:
                      "This will report and block this user and end the call. No audio, video, or screenshot will be recorded or submitted.",
                  })}
                </p>

                <div className="callroom-report-actions">
                  <button
                    type="button"
                    className="callroom-report-cancel"
                    onClick={() => setModerationStep("reason")}
                    disabled={reportSending}
                  >
                    {t("common.cancel", { defaultValue: "Cancel" })}
                  </button>
                  <button
                    type="button"
                    className="callroom-report-confirm"
                    onClick={submitModerationAction}
                    disabled={reportSending}
                  >
                    {reportSending
                      ? t("reports.submitting", { defaultValue: "Sending..." })
                      : t("common.confirm", { defaultValue: "Confirm" })}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </SensitiveContentGuard>

      <LivoraToast toast={toast} />
    </div>
  );
}
